'use client'

import { calculateAllInOrderTotal, type AllInOrderPricing } from '@/lib/fee-calculation'

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { setAffiliateCode } from '@/lib/affiliate-tracking'
import { useTimedCart } from '@/context/TimedCartContext'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import { ArrowLeft, MapPin, Calendar, X, Star } from 'lucide-react'
import UnifiedSeatMap from '@/components/timed-ui/booking/seat-map/UnifiedSeatMap'
import { buildPerTableIndex, isPerTableSection, perTableShare, isUnitSection as isUnitLayoutSection, type PerTableIndex } from '@/lib/seat-map/seat-pricing'
import { searchBestAvailable, type BestAvailableSearch, type BestAvailableSeat } from '@/lib/seat-map/best-available-search'
import { holdWithRetry, type HoldOutcome } from '@/lib/seat-map/hold-with-retry'
import { startSeatCartAttempt, type SeatCartAttempt } from '@/lib/seat-map/seat-cart-attempt'
import { countSingleSeats, isUnitBookingMode } from '@/lib/seat-map/seat-request-cap'
import { seasonPassSeatPrice } from '@/lib/seasons/season-seat-price'
import { newHoldSessionId, isLegacyHoldSessionId } from '@/lib/cart/hold-session'
import RichDescription from '@/components/timed-ui/shared/RichDescription'
import type { UnifiedSeatMapHandle } from '@/components/timed-ui/booking/seat-map/UnifiedSeatMap'
import LiveSeatMapMinimap from '@/components/timed-ui/booking/seat-map/LiveSeatMapMinimap'
import { createMapTransformStore } from '@/lib/seat-map/transform-store'
import SeatTooltip from '@/components/timed-ui/booking/seat-map/SeatTooltip'
import SeatSelectionSummary from '@/components/timed-ui/booking/seat-map/SeatSelectionSummary'
import BestAvailablePanel from '@/components/timed-ui/booking/seat-map/BestAvailablePanel'
import HoldTimer from '@/components/timed-ui/booking/seat-map/HoldTimer'
import type {
  SeatMapOverviewResponse,
  SeatDetail,
  SeatCategory,
  ChartLayoutData,
  SectionOverview,
  ComputedSeatPosition,
  VenueShape,
  LayoutStage,
  DecorativeShape,
  LayoutLabel,
  LayoutImage,
  FloorInfo,
} from '@/types/seating'

interface EventInfo {
  id: string
  slug: string
  name: string
  startDate: string
  endDate?: string
  imageUrl?: string
  timezone?: string
  hideBestAvailable?: boolean
  maxTicketsPerOrder?: number
  holdExpirationMin?: number
  showSoldCount?: boolean
  venue: {
    name: string
    address?: string | null
    city?: string | null
    state?: string | null
  }
  ticketTypes: Array<{
    id: string
    name: string
    description?: string
    price: number
    allInPrice: number
  }>
  // Server-normalized: ticketTypeId resolved against the full ticket list
  // (legacy ticketTypeIndex entries are translated before reaching here).
  categoryMapping?: Array<{
    categoryId: string
    ticketTypeId?: string
    /** Resolved to a real type that this channel cannot sell (box-office only). */
    restrictedElsewhere?: boolean
  }>
}

// Extended seat info that tracks which section each seat came from
interface SelectedSeatInfo extends SeatDetail {
  sectionKey?: string
  sectionName?: string
  sectionType?: string
  ticketTypeId?: string
  ticketTypeName?: string
  ticketTypePrice?: number
  ticketTypeAllInPrice?: number
  allInPrice: number  // all-in price computed at selection time — single source of truth
}

interface SeasonMode {
  seasonId: string
  seasonName: string
  passType: 'full' | 'flex' | 'package'
  seasonTicketTypeId?: string
  /**
   * The games a FLEX buyer chose, already validated against the season by the
   * server page. `null` on full and package passes, which cover a fixed list.
   * This is what ends up on the cart line as `seasonSelectedEventIds` — the
   * seat picker used to send nothing, and a flex pass bought here was issued
   * against every game in the season or against none at all.
   */
  selectedEventIds?: string[] | null
  selectedEventNames?: string[] | null
}

interface SelectSeatsClientProps {
  event: EventInfo
  initialOverview?: SeatMapOverviewResponse
  seasonMode?: SeasonMode
  /** The fee inputs the CHARGE uses, so the selection total is computed not summed. */
  orderPricing?: AllInOrderPricing
}

// A GA zone mapped only to box-office-only ticket types cannot be sold here.
// Dropping it is the honest outcome: the previous behaviour priced it from an
// unrelated ticket type (a $30 zone advertised at $56).
const sellableHere = (list: SectionOverview[]): SectionOverview[] =>
  list.filter(s => !s.channelUnavailable)

// Whether the browser's cart (TimedCartContext's `ticketCart`) has any line
// for this event — read straight from storage, because the session id is
// chosen before the cart context has loaded.
function browserCartHasEvent(eventId: string): boolean {
  try {
    const saved = JSON.parse(localStorage.getItem('ticketCart') || 'null') as { items?: Array<{ eventId?: string }> } | null
    return !!saved?.items?.some(i => i.eventId === eventId)
  } catch {
    return true // unreadable: keep whatever id is there
  }
}

// The canvas draws from layout.sections, NOT from the overview list, so a zone
// dropped from the list alone stayed painted on the map and clickable. Prune
// both together.
const prunedLayout = (
  layout: ChartLayoutData | null | undefined,
  secs: SectionOverview[],
): ChartLayoutData | null => {
  if (!layout) return null
  const drop = new Set(secs.filter(s => s.channelUnavailable).map(s => s.sectionKey))
  if (drop.size === 0) return layout
  return { ...layout, sections: layout.sections.filter(ls => !drop.has(ls.key)) }
}

export default function SelectSeatsClient({ event, initialOverview, seasonMode, orderPricing}: SelectSeatsClientProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  // Admin preview of a draft: the seat-map APIs check the admin session.
  const previewQs = searchParams?.get('preview') === '1' ? 'preview=1' : ''
  const formatPrice = useFormatPrice()
  const { addItem, removeItem: removeCartItem, cart } = useTimedCart()
  const mapRef = useRef<UnifiedSeatMapHandle>(null)
  // Height of the phone's fixed bottom panel (Best Available, or the selected-
  // seats bar), so the map frames itself ABOVE it. 0 on desktop (lg:hidden).
  const [mobileBottomH, setMobileBottomH] = useState(0)
  const mobileBottomRo = useRef<ResizeObserver | null>(null)
  const mobileBottomRef = useCallback((el: HTMLDivElement | null) => {
    mobileBottomRo.current?.disconnect()
    if (!el) { setMobileBottomH(0); return }
    const measure = () => setMobileBottomH(el.offsetHeight)
    measure()
    if (typeof ResizeObserver !== 'undefined') {
      mobileBottomRo.current = new ResizeObserver(measure)
      mobileBottomRo.current.observe(el)
    }
  }, [])
  const bundledSeatDataRef = useRef<Record<string, unknown> | null>(null)

  // Capture affiliate ref from URL params
  useEffect(() => {
    const ref = searchParams.get('ref') || (searchParams.get('utm_medium') === 'affiliate' ? searchParams.get('utm_source') : null)
    if (ref) {
      setAffiliateCode(event.id, ref)
      fetch('/api/affiliates/track-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ affiliateCode: ref, eventId: event.id }),
      }).catch(() => {})
    }
  }, [searchParams, event.id])

  // State — skip loading if we have server-side data
  const [loading, setLoading] = useState(!initialOverview)
  const [error, setError] = useState<string | null>(null)

  // Toast system
  const [toastMessage, setToastMessage] = useState<string | null>(null)
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showToast = useCallback((message: string) => {
    setToastMessage(message)
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => setToastMessage(null), 4000)
  }, [])

  // Enrich section minPrice/maxPrice with all-in prices from matching ticket type.
  // Section overview prices come from EventSeatStatus which may be stale — override with
  // the ticket type's all-in price (the admin-configured source of truth).
  const enrichWithAllInPrices = useCallback((secs: SectionOverview[], cats: SeatCategory[]) => {
    const catNameMap = new Map(cats.map(c => [c.id, c.name]))
    const tts = event.ticketTypes
    // A category can map to MULTIPLE ticket types (Adult/Child GA) — collect
    // them all so the section hover shows the true price RANGE, not just the
    // first mapping's price.
    const mappedTtsByCat = new Map<string, typeof tts>()
    // Categories whose mapping resolved to a real ticket type that cannot be
    // sold on this channel. That is NOT the same as "unmapped", and the two
    // must not share a code path: unmapped falls back to name matching, while
    // this one must not be offered here at all.
    const catsRestricted = new Set<string>()
    for (const m of (event.categoryMapping || [])) {
      if (m.categoryId && m.restrictedElsewhere) catsRestricted.add(m.categoryId)
      const tt = m.ticketTypeId ? tts.find(t => t.id === m.ticketTypeId) : undefined
      if (tt && !(tt as { isAddon?: boolean }).isAddon) {
        const arr = mappedTtsByCat.get(m.categoryId) || []
        if (!arr.some(x => x.id === tt.id)) arr.push(tt)
        mappedTtsByCat.set(m.categoryId, arr)
      }
    }
    return secs.map(s => {
      // No section-level category (e.g. per-ROW categories): the ESS-derived
      // min/max already reflect the real baked prices — keep them. Stamping
      // ticketTypes[0] here painted "$650" on sections priced $45-$65.
      if (!s.categoryId) return s
      // Mapping first (authoritative), then 4-level name matching. No
      // first-ticket fallback — an unresolvable category keeps ESS prices.
      const catName = catNameMap.get(s.categoryId)
      const mapped = mappedTtsByCat.get(s.categoryId)
      // Mapped, but to nothing sellable on this channel: drop the section
      // rather than guess. Name matching here is what advertised a $30
      // box-office-only zone at an unrelated type's $56.
      if (!mapped && catsRestricted.has(s.categoryId)) {
        return { ...s, channelUnavailable: true }
      }
      const nameMatched = !mapped && catName
        ? (tts.find(t => t.name === catName)
            || tts.find(t => t.name.toLowerCase() === catName.toLowerCase())
            || tts.find(t =>
                t.name.toLowerCase().includes(catName.toLowerCase()) ||
                catName.toLowerCase().includes(t.name.toLowerCase())))
        : undefined
      const candidates = (mapped || (nameMatched ? [nameMatched] : [])).filter(t => t.price > 0)
      if (candidates.length === 0) return s
      const isWholeTable = (s.sectionType === 'TABLE' || s.sectionType === 'BOOTH')
        && s.bookingMode !== 'by-seat'
      const displayPriceFor = (tt: typeof tts[0]): number => {
        if (isWholeTable) {
          if (isPerTableSection(s)) {
            // Admin price IS the table total — just apply fee ratio
            return tt.allInPrice
          }
          // Admin price is per-seat — multiply by capacity for table total
          return s.capacity > 1
            ? Math.round(tt.allInPrice * s.capacity * 100) / 100
            : tt.allInPrice
        }
        return tt.allInPrice
      }
      const prices = candidates.map(displayPriceFor)
      return {
        ...s,
        minPrice: Math.min(...prices),
        maxPrice: Math.max(...prices),
      }
    })
  }, [event.ticketTypes, event.categoryMapping])

  // Overview data — hydrate from server-side props if available
  const [layout, setLayout] = useState<ChartLayoutData | null>(() =>
    initialOverview
      ? prunedLayout(
          initialOverview.layout,
          enrichWithAllInPrices(initialOverview.sections, initialOverview.categories),
        )
      : null
  )
  const [sections, setSections] = useState<SectionOverview[]>(() =>
    initialOverview ? sellableHere(enrichWithAllInPrices(initialOverview.sections, initialOverview.categories)) : []
  )
  const [categories, setCategories] = useState<SeatCategory[]>(initialOverview?.categories || [])
  const [totalAvailable, setTotalAvailable] = useState(initialOverview?.totalAvailable || 0)
  const [categoryAvailability, setCategoryAvailability] = useState<Record<string, number>>(initialOverview?.categoryAvailability || {})
  const [venueShape, setVenueShape] = useState<VenueShape | undefined>(() => {
    if (initialOverview?.layout && (initialOverview.layout as Record<string, unknown>).venueShape) {
      return (initialOverview.layout as Record<string, unknown>).venueShape as VenueShape
    }
    return undefined
  })
  const [chartStage, setChartStage] = useState<LayoutStage | undefined>(initialOverview?.layout?.stage)
  const [decorativeShapes, setDecorativeShapes] = useState<DecorativeShape[]>(initialOverview?.layout?.shapes || [])
  const [layoutLabels, setLayoutLabels] = useState<LayoutLabel[]>(initialOverview?.layout?.labels || [])
  const [layoutImages, setLayoutImages] = useState<LayoutImage[]>(initialOverview?.layout?.images || [])
  const [backgroundImageUrl] = useState<string | null>(initialOverview?.backgroundImageUrl || null)

  // Floor/level data
  const [floors, setFloors] = useState<FloorInfo[]>(() => {
    if (initialOverview?.floors && initialOverview.floors.length > 1) return initialOverview.floors
    return []
  })
  const [activeFloor, setActiveFloor] = useState<number | null>(null)

  // Direct categoryId → ticketTypes[] map using the event's stored categoryMapping.
  // Stores ALL ticket types per category (e.g., VIP Adult, VIP Child, VIP Senior).
  //
  // Mapping entries arrive normalized to ticketTypeId (the server resolves
  // legacy index entries against the FULL ticket-type list before filtering
  // to visible types) — matching by id here is what makes the mapping
  // immune to ordering/filtering of this client-side array. Add-on guard
  // stays as a belt-and-braces check against bad stored config.
  const categoryToTicketTypes = useMemo(() => {
    const map = new Map<string, Array<typeof event.ticketTypes[0]>>()
    for (const m of (event.categoryMapping || [])) {
      const tt = m.ticketTypeId ? event.ticketTypes.find(t => t.id === m.ticketTypeId) : undefined
      if (tt && !(tt as any).isAddon) {
        const existing = map.get(m.categoryId) || []
        existing.push(tt)
        map.set(m.categoryId, existing)
      }
    }
    return map
  }, [event.categoryMapping, event.ticketTypes])

  // Resolve ticket type for a category: categoryMapping first, then name-based fallback.
  // This ensures events WITHOUT explicit categoryMapping still match correctly.
  // CRITICAL: every fallback path filters out addon ticket types. Without this
  // filter, an event with addons (e.g. merch, donations) configured as
  // TicketType records can have its seats misassigned to an addon's
  // ticketTypeId — orders show the right price (charged from the seat) but
  // point to the wrong type, breaking inventory + reports.
  const resolveTicketType = useCallback((categoryId: string | null | undefined, categoryName?: string | null) => {
    const seatableTypes = event.ticketTypes.filter(t => !(t as any).isAddon)
    if (!categoryId) return seatableTypes[0] || null
    // 1. Try explicit categoryMapping (and skip mapped result if it points at an addon — guard against bad config)
    const mapped = categoryToTicketTypes.get(categoryId)
    if (mapped && mapped.length > 0) {
      const nonAddon = mapped.find(m => !(m as any).isAddon)
      if (nonAddon) return nonAddon
    }
    // 2. Name-based fallback (4-level matching) — only against non-addon types
    const catName = categoryName || categories.find(c => c.id === categoryId)?.name
    if (catName) {
      return seatableTypes.find(t => t.name === catName)
        || seatableTypes.find(t => t.name.toLowerCase() === catName.toLowerCase())
        || seatableTypes.find(t =>
            t.name.toLowerCase().includes(catName.toLowerCase()) ||
            catName.toLowerCase().includes(t.name.toLowerCase()))
        || seatableTypes[0] || null
    }
    return seatableTypes[0] || null
  }, [categoryToTicketTypes, categories, event.ticketTypes])

  // Category color → price for the customer pricing legend (C1). Section fills
  // are colored by category; this maps those colors to their tier price.
  //
  // A per-table category's type price is the WHOLE table, and checkout levies
  // fees on each seat's share, so tt.allInPrice (one ticket) understated it:
  // a $600 six-top with $5 per ticket read $605 here and $630 on the map card
  // and Add to Cart. Price it as the whole table, the way the cart totals it.
  const categoryLegend = useMemo(() => {
    return categories
      .map(c => {
        const tt = resolveTicketType(c.id, c.name)
        let price = tt?.allInPrice ?? tt?.price ?? null
        const catSections = sections.filter(s => s.categoryId === c.id)
        const perTable = catSections.length > 0 && catSections.every(s => isPerTableSection(s))
        if (perTable) {
          const tableTotals = catSections
            .map(s => {
              const count = s.capacity || 0
              if (orderPricing && tt && tt.price > 0 && count > 0) {
                return calculateAllInOrderTotal(tt.price, count, orderPricing)
              }
              return s.allInMinPrice ?? null
            })
            .filter((n): n is number => n != null && n > 0)
          if (tableTotals.length > 0) price = Math.min(...tableTotals)
        }
        return { id: c.id, name: c.name, color: (c as any).color || '#94a3b8', price, perTable }
      })
      .filter((c): c is { id: string; name: string; color: string; price: number; perTable: boolean } => c.price != null)
  }, [categories, sections, resolveTicketType, orderPricing])

  // Seat selection
  const [selectedSeats, setSelectedSeats] = useState<Map<string, SelectedSeatInfo>>(new Map())
  const [isAdding, setIsAdding] = useState(false)

  // Desired ticket quantity — shared between the Best Available picker and the
  // manual-selection cap so "pick 2" actually limits manual selection to 2.
  const maxPerOrder = event.maxTicketsPerOrder && event.maxTicketsPerOrder > 0 ? event.maxTicketsPerOrder : 10
  const [desiredQty, setDesiredQty] = useState(() => Math.min(2, maxPerOrder))
  const setDesiredQtyClamped = (q: number) => setDesiredQty(Math.max(1, Math.min(maxPerOrder, q || 1)))
  // Cap manual selection at the chosen quantity when the picker is shown;
  // otherwise allow up to the event's per-order limit.
  const selectionCap = event.hideBestAvailable ? maxPerOrder : Math.min(desiredQty, maxPerOrder)
  const [navigating, setNavigating] = useState(false)
  const [holdExpiresAt, setHoldExpiresAt] = useState<Date | null>(null)

  // Ticket type picker (shown when a category has multiple ticket types)
  const [ticketTypePicker, setTicketTypePicker] = useState<{
    seat: ComputedSeatPosition
    ticketTypes: Array<typeof event.ticketTypes[0]>
    selectedTicketTypeId: string
  } | null>(null)

  // Prefetch checkout page for smooth transition
  useEffect(() => { router.prefetch('/checkout') }, [router])

  // Tooltip
  const [tooltipSeat, setTooltipSeat] = useState<SeatDetail | null>(null)
  const [tooltipSectionKey, setTooltipSectionKey] = useState<string | null>(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })
  const [tooltipAllInPrice, setTooltipAllInPrice] = useState<number | null>(null)

  // Fee ratio for all-in display (ratio is constant across all TTs in an event — percentage-only fees)
  const feeRatio = useMemo(() => {
    const tt = event.ticketTypes[0]
    return tt && tt.price > 0 ? tt.allInPrice / tt.price : 1
  }, [event.ticketTypes])

  /**
   * The advertised single-seat all-in price for a base price: the same figure
   * the server stamps on every ticket type (tt.allInPrice, which the price key
   * shows) and the same helper the Add to Cart total uses, at a quantity of one.
   *
   * feeRatio is only right for percentage fees. With a fixed fee it is taken
   * from the FIRST ticket type, so Africa Gift Box ($2 per-ticket fee, $50 VIP
   * first) priced a $15 Reserved seat at $15.60 while the key said $17.00.
   * It stays as the fallback for callers with no fee inputs (season mode).
   */
  const allInFor = useCallback((basePrice: number): number => {
    if (!(basePrice > 0)) return 0
    if (orderPricing) return calculateAllInOrderTotal(basePrice, 1, orderPricing)
    return Math.round(basePrice * feeRatio * 100) / 100
  }, [orderPricing, feeRatio])

  /**
   * The all-in total for a set of seats, computed rather than summed.
   *
   * seat.allInPrice is the advertised single-seat total including fixed fees. A
   * fixed fee is per ORDER unless EventFee.perTicket is set, so adding those up
   * charged the fee once per seat in the display while checkout charged it once:
   * a four-seat selection over-stated the total by three fees plus their tax.
   */
  const allInTotalFor = useCallback((seats: SelectedSeatInfo[]): number => {
    const subtotal = seats.reduce((sum, s) => sum + (s.price || 0), 0)
    if (subtotal <= 0) return 0
    // Without the fee inputs, keep the old sum rather than quoting a bare
    // subtotal with no fees, which would understate every selection.
    if (!orderPricing) {
      return Math.round(seats.reduce((t, s) => t + s.allInPrice, 0) * 100) / 100
    }
    return calculateAllInOrderTotal(subtotal, seats.length, orderPricing)
  }, [orderPricing])

  // Minimap / focused section tracking
  const [focusedSectionKey, setFocusedSectionKey] = useState<string | null>(null)
  const [previewSeatIds, setPreviewSeatIds] = useState<Set<string> | undefined>(undefined)
  const [showViewImage, setShowViewImage] = useState(false)
  // Mobile bottom sheet: full per-seat summary above the CTA bar
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false)
  // Legend hover: fade other categories' seats on the map
  const [highlightCategoryId, setHighlightCategoryId] = useState<string | null>(null)
  // Visual-treatment preview switch (?viz=1): sold-seat slashes + on-map
  // section prices, pending owner sign-off before becoming default
  const [vizPreview] = useState(() =>
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('viz') === '1')
  // The map's live pan/zoom, for the minimap only — kept out of this
  // component's state so a pan or pinch does not re-render the whole page
  // on every frame (lib/seat-map/transform-store).
  const [mapTransformStore] = useState(() => createMapTransformStore())
  const [mapContainerSize, setMapContainerSize] = useState({ width: 800, height: 600 })
  // Callback ref: the map area mounts only after the overview has loaded, so a
  // mount-time effect never saw it and the minimap worked from 800x600.
  const mapContainerRo = useRef<ResizeObserver | null>(null)
  const mapContainerRef = useCallback((el: HTMLDivElement | null) => {
    mapContainerRo.current?.disconnect()
    if (!el || typeof ResizeObserver === 'undefined') return
    mapContainerRo.current = new ResizeObserver(entries => {
      const e = entries[0]
      if (e) setMapContainerSize({ width: e.contentRect.width, height: e.contentRect.height })
    })
    mapContainerRo.current.observe(el)
  }, [])
  // Short screens (landscape phones, and small portrait phones such as the
  // iPhone SE at 667px, where the 44px-target form took over half the screen):
  // the Best Available form collapses to a single button so the map keeps
  // most of the height.
  const [shortViewport, setShortViewport] = useState(false)
  const [bestSheetOpen, setBestSheetOpen] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(max-height: 699px)')
    const update = () => setShortViewport(mq.matches)
    update()
    mq.addEventListener?.('change', update)
    return () => mq.removeEventListener?.('change', update)
  }, [])

  // Fullscreen map (desktop only)
  const [isFullscreenMap, setIsFullscreenMap] = useState(false)

  // GA/Table/Booth section picker. selectedTicketTypeId carries the buyer's
  // choice when the section's category maps to multiple ticket types
  // (Adult/Child GA) — mirrors the per-seat ticket type picker.
  const [unitPickerSection, setUnitPickerSection] = useState<{
    sectionKey: string
    mode: 'GA' | 'WHOLE_TABLE' | 'BOOTH'
    selectedTicketTypeId?: string
  } | null>(null)
  const [unitPickerQty, setUnitPickerQty] = useState(1)
  // Per-ticket-type quantities for GA sections whose category maps to
  // multiple types (Adult/Child) — lets a buyer add e.g. 2 Adult + 1 Child
  // in one confirm instead of re-opening the picker per type.
  const [unitPickerTypeQty, setUnitPickerTypeQty] = useState<Record<string, number>>({})
  const [unitPickerLoading, setUnitPickerLoading] = useState(false)

  // Toast cleanup on unmount
  useEffect(() => {
    return () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current) }
  }, [])

  // Status polling
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // Start 15 s back: the section payload can be a CDN copy up to 10 s old, and
  // a release inside that window would otherwise never reach this viewer.
  // Deltas apply idempotently, so re-receiving a few seconds is harmless.
  const lastPollRef = useRef<string>(new Date(Date.now() - 15_000).toISOString())
  const pollFailCountRef = useRef(0)

  // Refs for volatile values used inside polling interval
  const selectedSeatsSizeRef = useRef(selectedSeats.size)

  // Session ID (stable per page load). It is the ONLY proof that this
  // browser's holds and server cart are its own — release, extend and checkout
  // act on whatever id a request presents — so it is 128 random bits and never
  // appears in any public response (lib/cart/hold-session).
  const sessionPreexistedRef = useRef(false)
  const [sessionId] = useState(() => {
    if (typeof window !== 'undefined') {
      const key = `seat-session-${event.id}`
      const existing = localStorage.getItem(key)
      // The pre-2026-11 id was guessable in principle (timestamp + Math.random).
      // Replace it, unless this event already has tickets in the browser's
      // cart: those holds and that server cart are keyed by it.
      if (existing && !(isLegacyHoldSessionId(existing) && !browserCartHasEvent(event.id))) {
        sessionPreexistedRef.current = true
        return existing
      }
      const id = newHoldSessionId('seat')
      localStorage.setItem(key, id)
      return id
    }
    return `seat-${Date.now()}`
  })

  // Sync volatile refs
  useEffect(() => { selectedSeatsSizeRef.current = selectedSeats.size }, [selectedSeats.size])

  // Format event date
  const eventDate = new Date(event.startDate)
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: event.timezone || undefined,
  })
  const formattedTime = eventDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: event.timezone || undefined,
  })
  const formattedDoorTime = event.doorTime ? new Date(event.doorTime).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: event.timezone || undefined,
  }) : null

  // Load seat map overview with timeout protection
  const loadOverview = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 30000)
      const mapUrl = seasonMode
        ? `/api/seasons/${seasonMode.seasonId}/seat-map`
        : `/api/events/${event.id}/seat-map?include=seats${previewQs ? `&${previewQs}` : ''}`
      const res = await fetch(mapUrl, {
        signal: controller.signal,
      })
      clearTimeout(timeout)
      const data = await res.json()
      if (data.success && data.data) {
        const overview: SeatMapOverviewResponse = data.data
        const enriched = enrichWithAllInPrices(overview.sections, overview.categories)
        setLayout(prunedLayout(overview.layout, enriched))
        setCategories(overview.categories)
        setSections(sellableHere(enriched))
        setTotalAvailable(overview.totalAvailable)
        setCategoryAvailability(overview.categoryAvailability || {})
        // Extract venue shape from layout data if present
        if (overview.layout && (overview.layout as Record<string, unknown>).venueShape) {
          setVenueShape((overview.layout as Record<string, unknown>).venueShape as VenueShape)
        }
        if (overview.layout?.stage) setChartStage(overview.layout.stage)
        if (overview.layout?.shapes) setDecorativeShapes(overview.layout.shapes)
        if (overview.layout?.labels) setLayoutLabels(overview.layout.labels)
        if (overview.layout?.images) setLayoutImages(overview.layout.images)
        // Store bundled seat data for instant cache pre-population
        if ((overview as any).seatSections) {
          bundledSeatDataRef.current = (overview as any).seatSections
        }
        // Floor/level data
        if (overview.floors && overview.floors.length > 1) {
          setFloors(overview.floors)
          // Default to "All Levels" (null) so user sees the full venue
        }
      } else {
        setError(data.error || 'Failed to load seat map')
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setError('Seat map took too long to load. Please try again.')
      } else {
        setError('Failed to load seat map')
      }
    } finally {
      setLoading(false)
    }
  }, [event.id, enrichWithAllInPrices])

  useEffect(() => {
    // Skip fetch if server already provided the overview data
    if (initialOverview) return
    loadOverview()
  }, [loadOverview, initialOverview])

  // Every section's seats in ONE request when the page arrived with its
  // overview (the normal path). Without it the map fetched each section
  // separately — 38 requests for a bowl, 12 at a time, each a few hundred ms
  // of database time — and only settled seconds after it first drew. The page
  // starts this exact request from an inline script while the scripts load
  // (page.tsx); its promise is taken over here, so the response is usually
  // already in hand. While it is in flight the map holds its per-section
  // loading; if it fails, the map falls back to exactly that.
  const seatBundleUrl = `/api/events/${event.id}/seat-map?seats=only${previewQs ? `&${previewQs}` : ''}`
  // Only the sections the map loads seats for, exactly as the per-section
  // path did: a whole table, booth or GA zone is sold as a unit and draws no
  // individual seats (SectionSeatCache.shouldLoadSeats). A chart of tables
  // and GA zones only needs no seat request at all.
  const seatedSectionKeys = useMemo(() => new Set((initialOverview?.layout?.sections || [])
    .filter(ls => !isUnitLayoutSection({ sectionType: ls.sectionType, tableConfig: ls.tableConfig }))
    .map(ls => ls.key)), [initialOverview])
  const wantsSeatBundle = !!initialOverview && !seasonMode && seatedSectionKeys.size > 0
  const [seatBundle, setSeatBundle] = useState<Record<string, unknown> | null>(null)
  const [seatBundlePending, setSeatBundlePending] = useState(() => wantsSeatBundle)
  useEffect(() => {
    if (!wantsSeatBundle) return
    let cancelled = false
    const w = window as Window & { __ptSeatBundle?: { url: string; promise: Promise<unknown> } }
    const early = w.__ptSeatBundle
    delete w.__ptSeatBundle // one page view's response, used once
    const request: Promise<{ success?: boolean; data?: { seatSections?: unknown } } | null> =
      early && early.url === seatBundleUrl
        ? (early.promise as Promise<{ success?: boolean; data?: { seatSections?: unknown } } | null>)
        : fetch(seatBundleUrl).then(res => (res.ok ? res.json() : null))
    request
      .then(data => {
        if (cancelled) return
        const seatSections = data?.success ? data.data?.seatSections : null
        if (!seatSections || typeof seatSections !== 'object') return
        const seated: Record<string, unknown> = {}
        for (const [key, sec] of Object.entries(seatSections as Record<string, unknown>)) {
          if (seatedSectionKeys.has(key)) seated[key] = sec
        }
        setSeatBundle(seated)
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setSeatBundlePending(false) })
    return () => { cancelled = true }
  }, [wantsSeatBundle, seatedSectionKeys, seatBundleUrl])

  // Hydrate selectedSeats from cart items when returning from checkout
  // This ensures the sidebar shows already-held seats
  const cartHydratedRef = useRef(false)
  useEffect(() => {
    if (cartHydratedRef.current) return
    const seatItems = cart.items.filter(i => i.seatId && i.eventId === event.id)
    if (seatItems.length === 0) return
    cartHydratedRef.current = true

    // Reconstruct selectedSeats from cart items
    const hydrated = new Map<string, SelectedSeatInfo>()
    for (const item of seatItems) {
      if (!item.seatId) continue
      // Use stored allInPrice from cart (already computed correctly), or look up ticket type
      const tt = event.ticketTypes.find(t => t.id === item.ticketTypeId)
      const allIn = item.allInPrice ?? tt?.allInPrice ?? allInFor(item.price ?? 0)

      // Resolve sectionKey: prefer stored value, fall back to matching section by name
      let sectionKey = item.seatData?.sectionKey
      const sectionName = item.seatData?.sectionName
      const sectionType = item.seatData?.sectionType
      if (!sectionKey && sectionName) {
        // Look up sectionKey from the loaded sections data by matching name
        const matchedSection = sections.find(s => s.name === sectionName)
        if (matchedSection) sectionKey = matchedSection.sectionKey
      }

      hydrated.set(item.seatId, {
        seatId: item.seatId,
        seatNumber: item.seatData?.seatNumber || '',
        seatIndex: 0,
        rowLabel: item.seatData?.rowLabel || '',
        rowNumber: 0,
        seatType: 'STANDARD',
        status: 'HELD',
        categoryId: null,
        category: item.seatData?.categoryName
          ? categories.find(c => c.name === item.seatData?.categoryName) || null
          : null,
        price: item.price,
        x: 0,
        y: 0,
        sectionKey: sectionKey || undefined,
        sectionName: sectionName || undefined,
        sectionType: sectionType || undefined,
        allInPrice: allIn,
      })
    }
    if (hydrated.size > 0) {
      setSelectedSeats(hydrated)
    }
  }, [cart.items, event.id, categories, allInFor, sections])


  // Fullscreen: ESC key handler and body scroll lock
  useEffect(() => {
    if (!isFullscreenMap) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreenMap(false)
    }
    document.addEventListener('keydown', handleEsc)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = ''
    }
  }, [isFullscreenMap])

  // Status polling - apply changes to unified map cache
  // Uses refs so the interval doesn't restart on every selectedSeats change
  const selectedSeatsRef = useRef(selectedSeats)
  useEffect(() => { selectedSeatsRef.current = selectedSeats }, [selectedSeats])
  const showToastRef = useRef(showToast)
  useEffect(() => { showToastRef.current = showToast }, [showToast])

  // Seats this browser's session holds. Public seat data (bundle, sections,
  // status feed) carries no session ids — anyone could read them and release
  // or buy another buyer's seats with them (lib/cart/hold-session) — so "this
  // held seat is mine" comes only from answers to this session's own
  // requests: the hold response, and /seats/my-holds (page load, another tab's
  // hold, or one of these seats leaving HELD).
  const ownHoldsRef = useRef<Set<string>>(new Set())
  const ownHoldAddedAtRef = useRef<Map<string, number>>(new Map())
  const holdingRef = useRef<Set<string>>(new Set()) // seats with a hold request in flight
  const ownHoldsOnMapRef = useRef(false) // the map currently marks some seats mine
  const pushOwnHolds = useCallback(() => {
    const map = mapRef.current
    // Nothing to mark and nothing marked: no repaint for a buyer with no holds.
    if (!map?.setOwnHolds || (ownHoldsRef.current.size === 0 && !ownHoldsOnMapRef.current)) return
    map.setOwnHolds(Array.from(ownHoldsRef.current))
    ownHoldsOnMapRef.current = ownHoldsRef.current.size > 0
  }, [])
  const markOwnHolds = useCallback((seatIds: string[], own: boolean) => {
    if (seatIds.length === 0) return
    const now = Date.now()
    for (const id of seatIds) {
      if (own) { ownHoldsRef.current.add(id); ownHoldAddedAtRef.current.set(id, now) }
      else { ownHoldsRef.current.delete(id); ownHoldAddedAtRef.current.delete(id) }
    }
    pushOwnHolds()
    // Other tabs of this browser share the session: tell them to re-read.
    try { localStorage.setItem(`seat-holds-changed-${event.id}`, String(now)) } catch { /* private mode */ }
  }, [event.id, pushOwnHolds])
  const ownHoldsSeqRef = useRef(0)
  const refreshOwnHolds = useCallback(async () => {
    if (seasonMode) return // a pass holds each game's seat; the season map shows them as taken
    const seq = ++ownHoldsSeqRef.current
    const startedAt = Date.now()
    try {
      const res = await fetch(`/api/events/${event.id}/seats/my-holds?sessionId=${encodeURIComponent(sessionId)}`, { cache: 'no-store' })
      const data = await res.json()
      if (seq !== ownHoldsSeqRef.current || !data?.success) return
      const next = new Set<string>((data.data?.seats || []).map((s: { seatId: string }) => s.seatId))
      // A hold this tab made while the request was in flight is not in it yet.
      for (const [id, at] of ownHoldAddedAtRef.current) if (at >= startedAt) next.add(id)
      for (const id of Array.from(ownHoldAddedAtRef.current.keys())) if (!next.has(id)) ownHoldAddedAtRef.current.delete(id)
      ownHoldsRef.current = next
      pushOwnHolds()
    } catch { /* the next trigger tries again */ }
  }, [event.id, sessionId, seasonMode, pushOwnHolds])
  const refreshOwnHoldsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleOwnHoldsRefresh = useCallback(() => {
    if (refreshOwnHoldsTimerRef.current) return
    refreshOwnHoldsTimerRef.current = setTimeout(() => {
      refreshOwnHoldsTimerRef.current = null
      void refreshOwnHolds()
    }, 800)
  }, [refreshOwnHolds])
  useEffect(() => () => { if (refreshOwnHoldsTimerRef.current) clearTimeout(refreshOwnHoldsTimerRef.current) }, [])
  // A returning session may already hold seats (cart, another tab): ask once.
  useEffect(() => {
    if (sessionPreexistedRef.current) void refreshOwnHolds()
  }, [refreshOwnHolds])
  // Another tab held or released: re-read this session's holds.
  useEffect(() => {
    const key = `seat-holds-changed-${event.id}`
    const onStorage = (e: StorageEvent) => { if (e.key === key) scheduleOwnHoldsRefresh() }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [event.id, scheduleOwnHoldsRefresh])
  // The map loads its seats after this page mounts: stamp them once they land.
  useEffect(() => { pushOwnHolds() }, [seatBundle, loading, pushOwnHolds])
  // Seats already in the browser's cart for this event are held under this
  // session: count them as ours before the first my-holds answer, so a poll
  // re-delivering their HELD state does not read as "taken by another customer".
  const ownHoldsSeededRef = useRef(false)
  useEffect(() => {
    if (ownHoldsSeededRef.current) return
    const ids = cart.items.filter(i => i.seatId && i.eventId === event.id).map(i => i.seatId!)
    if (ids.length === 0) return
    ownHoldsSeededRef.current = true
    for (const id of ids) ownHoldsRef.current.add(id)
    pushOwnHolds()
  }, [cart.items, event.id, pushOwnHolds])

  // One poll: seat deltas into the map cache, section counts into `sections`
  // (so "Sold Out" follows the seats), and "your seat was taken" toasts.
  const pollNow = useCallback(async () => {
    try {
      // Quantize `since` to a shared 2s bucket so every client polling in the
      // same window sends the IDENTICAL URL — the CDN (s-maxage=2) then collapses
      // thousands of polls into ~1 origin hit. Flooring (never rounding up) means
      // the worst case is re-receiving a couple seconds of changes, which apply
      // idempotently — no missed updates.
      const sinceMs = new Date(lastPollRef.current).getTime()
      const bucketedSince = Number.isFinite(sinceMs)
        ? new Date(Math.floor(sinceMs / 2000) * 2000).toISOString()
        : lastPollRef.current
      const res = await fetch(`/api/events/${event.id}/seats/status?since=${encodeURIComponent(bucketedSince)}`)
      const data = await res.json()
      if (data.success && data.data) {
        pollFailCountRef.current = 0
        lastPollRef.current = data.data.timestamp
        if (data.data.changes.length > 0) {
          const changes = data.data.changes as Array<{ seatId: string; status: string }>
          mapRef.current?.applyStatusChanges(changes)

          // Section counts drive the "Sold Out" label, the sold-out fill and the
          // table/booth picker. They were loaded once at page open; a release
          // after that never raised them again. (Season overviews count across
          // the whole season, so leave them alone there.)
          const counts = data.data.sections as Record<string, number> | undefined
          if (counts && !seasonMode) {
            setSections(prev => {
              let changed = false
              const next = prev.map(sec => {
                const available = counts[sec.sectionKey]
                if (available === undefined || available === sec.available) return sec
                changed = true
                return { ...sec, available }
              })
              return changed ? next : prev
            })
          }

          // One of our own holds is no longer HELD (expired, released from
          // the cart drawer, bought in another tab): re-read which are ours.
          if (changes.some(c => c.status !== 'HELD' && ownHoldsRef.current.has(c.seatId))) {
            scheduleOwnHoldsRefresh()
          }

          // Check if any locally selected seats were taken by someone else.
          // A seat this session holds (or is holding right now) is our own
          // hold landing — without that exclusion, every seat the buyer picked
          // flashed "taken by another customer" as soon as its hold wrote.
          // The feed names no session (lib/cart/hold-session); ownership comes
          // from our own hold / my-holds answers.
          const currentSelected = selectedSeatsRef.current
          if (currentSelected.size > 0) {
            const takenByOthers = changes.filter(c =>
              c.status !== 'FREE' &&
              currentSelected.has(c.seatId) &&
              !ownHoldsRef.current.has(c.seatId) &&
              !holdingRef.current.has(c.seatId)
            )
            if (takenByOthers.length > 0) {
              setSelectedSeats(prev => {
                const next = new Map(prev)
                for (const c of takenByOthers) {
                  const seat = next.get(c.seatId)
                  if (seat) {
                    showToastRef.current(`Seat ${seat.seatNumber}, Row ${seat.rowLabel} was taken by another customer`)
                  }
                  next.delete(c.seatId)
                }
                return next
              })
            }
          }
        }
      }
    } catch {
      pollFailCountRef.current++
      if (pollFailCountRef.current >= 3) {
        showToastRef.current('Connection issue - seat availability may be outdated')
        pollFailCountRef.current = 0
      }
    }
  }, [event.id, seasonMode, scheduleOwnHoldsRefresh])
  const pollNowRef = useRef(pollNow)
  useEffect(() => { pollNowRef.current = pollNow }, [pollNow])

  useEffect(() => {
    // Don't start polling until overview is loaded
    if (loading) return

    const POLL_INTERVAL = 3000 // the CDN collapses same-bucket polls, so this is cheap
    pollTimerRef.current = setInterval(() => { void pollNowRef.current() }, POLL_INTERVAL)

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    }
  }, [event.id, loading]) // Only restart when event changes or loading completes

  // The buyer's own hold or release changes the cart's seat list; poll right
  // after it lands so their own map and section counts move at once instead
  // of on the next tick. (The release request is fire-and-forget, hence the
  // short delay.)
  const cartSeatKey = cart.items
    .filter(i => i.eventId === event.id && i.seatId)
    .map(i => i.seatId)
    .sort()
    .join(',')
  useEffect(() => {
    if (loading) return
    const t = setTimeout(() => { void pollNowRef.current() }, 700)
    return () => clearTimeout(t)
  }, [cartSeatKey, loading])

  // Every sellable section reads 0 available (the overview's counts, kept
  // current by the poll): the sidebar says so instead of inviting a pick.
  const everythingSold = !loading && sections.length > 0 && sections.every(sec => (sec.available ?? 0) <= 0)

  // Section name lookup for seat info
  const sectionNameMap = useMemo(
    () => new Map(sections.map(s => [s.sectionKey, s.name])),
    [sections]
  )

  // Booking mode lookup for table enforcement
  const sectionBookingModes = useMemo(
    () => new Map(sections
      .filter(s => s.bookingMode || s.tableConfig)
      .map(s => [s.sectionKey, {
        bookingMode: s.bookingMode || s.tableConfig?.bookingMode,
        minOccupancy: s.tableConfig?.minOccupancy,
        pricingMode: s.tableConfig?.pricingMode,
      }])
    ),
    [sections]
  )

  // Check if a section is a unit section (whole-table, variable, GA, BOOTH, TABLE)
  const isUnitSection = useCallback((sectionKey: string): boolean => {
    const sd = sections.find(s => s.sectionKey === sectionKey)
    if (!sd) return false
    if (sd.sectionType === 'GA' || sd.sectionType === 'BOOTH') return true
    // TABLE sections default to whole-table unless explicitly set to by-seat
    if (sd.sectionType === 'TABLE') {
      const info = sectionBookingModes.get(sectionKey)
      return info?.bookingMode !== 'by-seat'
    }
    const info = sectionBookingModes.get(sectionKey)
    return info?.bookingMode === 'whole-table' || info?.bookingMode === 'variable'
  }, [sections, sectionBookingModes])

  // Seats at a table booked as a unit are outside the per-order seat cap, as
  // they are for the hold and the cart (lib/seat-map/seat-request-cap): two
  // six-seat tables are one 12-seat order. Only these seats meet the cap.
  const singleSeatCount = useMemo(() => {
    const unitKeys = new Set(
      Array.from(sectionBookingModes)
        .filter(([, info]) => isUnitBookingMode(info.bookingMode))
        .map(([key]) => key)
    )
    return countSingleSeats(Array.from(selectedSeats.keys()), id => selectedSeats.get(id)?.sectionKey, unitKeys)
  }, [selectedSeats, sectionBookingModes])

  // The divisor and the seat order for a per-table split must be the
  // SERVER's — every active seat in the section, in its order — never the
  // seats this surface happens to be able to see. The section endpoint
  // reports both (seatCount / pricingIndex); this caches them per section.
  // Same mechanism as the box office's loadPerTableIndex.
  const perTableIndexRef = useRef<Map<string, PerTableIndex>>(new Map())
  // The last Best Available pick and the search behind it. At hold time, an
  // untouched pick that collides with another buyer is re-searched and moved
  // to the next best seats instead of dead-ending (item 30).
  const lastBestAvailableRef = useRef<{ search: BestAvailableSearch; seatIds: Set<string> } | null>(null)
  const loadPerTableIndex = useCallback(async (sectionKey: string): Promise<PerTableIndex | null> => {
    const cached = perTableIndexRef.current.get(sectionKey)
    if (cached) return cached
    try {
      const url = seasonMode
        ? `/api/seasons/${seasonMode.seasonId}/seat-map/section/${sectionKey}`
        : `/api/events/${event.id}/seat-map/section/${sectionKey}${previewQs ? `?${previewQs}` : ''}`
      const res = await fetch(url)
      const data = await res.json()
      if (!data.success || !data.data) return null
      const entry = buildPerTableIndex(data.data)
      perTableIndexRef.current.set(sectionKey, entry)
      return entry
    } catch {
      return null
    }
  }, [event.id, seasonMode])

  // Add a seat to selection with a specific ticket type
  const addSeatToSelection = useCallback(async (seat: ComputedSeatPosition, ticketType?: typeof event.ticketTypes[0]) => {
    const sectionOverview = sections.find(s => s.sectionKey === seat.sectionKey)
    // Shared with the box office and the server charge path — a section only
    // divides when it is a TABLE/BOOTH explicitly set to per-table pricing.
    const isPerTable = isPerTableSection(sectionOverview)
    // Seat price FIRST: the charge path (cart) prefers the baked
    // EventSeatStatus price over the live ticket-type price, so display
    // must too — preferring the live price showed the NEW amount after an
    // admin repriced a ticket type post-publish while the cart still
    // charged the OLD baked one.
    //
    // SEASON MODE INVERTS THAT. A seat's baked price belongs to ONE GAME, and
    // a season pass is not a game — the season's own tier is the only price
    // that exists here, and it is what the server charges
    // (src/lib/seasons/price-season-items.ts).
    let basePrice = seasonMode
      ? seasonPassSeatPrice(ticketType?.price)
      : (seat.price ?? ticketType?.price ?? 0)
    // A per-table price is the WHOLE table. Split it the way the charge path
    // does — penny-exact, over the server's seat count and order — not by
    // uniform rounding over the overview capacity, which showed 6 x $108.33
    // = $649.98 for a table that rings up at $650.00.
    if (isPerTable && basePrice > 0 && seat.sectionKey) {
      const index = await loadPerTableIndex(seat.sectionKey)
      const count = index?.count ?? (sectionOverview?.capacity || 1)
      if (count > 1) {
        const i = index?.indexById.get(seat.seatId) ?? 0
        basePrice = perTableShare(basePrice, count, i)
      }
    }
    // Fees on the per-seat SHARE, the way checkout levies them: each seat is
    // its own cart line, so a per-ticket fee lands on every seat of a table,
    // not once on the table split six ways.
    const allIn = allInFor(basePrice)
    const seatInfo: SelectedSeatInfo = {
      seatId: seat.seatId,
      seatNumber: seat.seatNumber,
      seatIndex: seat.seatIndex,
      rowLabel: seat.rowLabel,
      rowNumber: seat.rowNumber,
      seatType: seat.seatType,
      status: 'FREE',
      categoryId: seat.categoryId,
      category: seat.category,
      price: basePrice,
      x: seat.absX,
      y: seat.absY,
      sectionKey: seat.sectionKey,
      sectionName: sectionNameMap.get(seat.sectionKey) || undefined,
      sectionType: sections.find(s => s.sectionKey === seat.sectionKey)?.sectionType || 'SEATED',
      ticketTypeId: ticketType?.id,
      ticketTypeName: ticketType?.name,
      // In a per-table section these MUST be the per-seat share, exactly as
      // handleSelectWholeUnit stamps them. The cart write prefers
      // ticketTypePrice over price (`seat.ticketTypePrice ?? seat.price ?? …`),
      // so stamping the raw type price sent the WHOLE TABLE amount to checkout
      // for every seat of a by-seat section priced per-table — even though the
      // map had just divided it correctly for display. Per-seat sections keep
      // the live ticket-type figures untouched.
      // Season mode stamps the resolved figures for the same reason: these
      // feed the cart line directly, and `ticketType.price` is already the
      // season tier price there.
      ticketTypePrice: isPerTable || seasonMode ? basePrice : ticketType?.price,
      ticketTypeAllInPrice: isPerTable || seasonMode ? allIn : ticketType?.allInPrice,
      allInPrice: allIn,
    }
    setSelectedSeats(prev => new Map(prev).set(seat.seatId, seatInfo))
  }, [sectionNameMap, sections, allInFor, loadPerTableIndex, seasonMode])

  // Seat click from unified map - pure client-side selection (no server holds)
  const handleSeatClick = useCallback((seat: ComputedSeatPosition) => {
    const isSelected = selectedSeats.has(seat.seatId)

    if (isSelected) {
      // For unit sections (whole-table, variable), deselect ALL seats in that section
      const seatInfo = selectedSeats.get(seat.seatId)
      const sKey = seat.sectionKey || seatInfo?.sectionKey
      const isUnit = (sKey && isUnitSection(sKey)) || seatInfo?.sectionType === 'TABLE'
      if (isUnit && sKey) {
        setSelectedSeats(prev => {
          const next = new Map(prev)
          for (const [id, s] of prev) {
            if ((s.sectionKey || s.sectionName) === sKey || s.sectionKey === sKey) {
              next.delete(id)
              const cartItem = cart.items.find(i => i.seatId === id)
              if (cartItem) removeCartItem(cartItem.id)
            }
          }
          return next
        })
        showToast('Entire table removed')
        return
      }
      // Deselect - remove from local state and cart if already added
      setSelectedSeats(prev => {
        const next = new Map(prev)
        next.delete(seat.seatId)
        return next
      })
      const cartItem = cart.items.find(i => i.seatId === seat.seatId)
      if (cartItem) removeCartItem(cartItem.id)
    } else {
      // Select - add to local state (no server call)
      if (singleSeatCount >= selectionCap) {
        // The quantity picker is a starting point, not a wall: clicking one
        // more seat grows the quantity automatically (a GA-seeded "2/2"
        // selection used to hard-block adding a reserved seat, making
        // mixed GA + reserved carts needlessly awkward). Only the event's
        // per-order limit is a hard stop.
        if (!event.hideBestAvailable && singleSeatCount < maxPerOrder) {
          setDesiredQty(q => Math.min(maxPerOrder, Math.max(q, singleSeatCount + 1)))
        } else {
          showToast(`Maximum ${maxPerOrder} seat${maxPerOrder !== 1 ? 's' : ''} per order`)
          return
        }
      }
      if (seat.status !== 'FREE') return

      // Check if this category has multiple ticket types
      const ticketTypes = seat.categoryId ? categoryToTicketTypes.get(seat.categoryId) : null
      if (ticketTypes && ticketTypes.length > 1) {
        // Show ticket type picker
        setTicketTypePicker({
          seat,
          ticketTypes,
          selectedTicketTypeId: ticketTypes[0].id,
        })
      } else {
        // Single or no ticket type — resolve via mapping or name fallback
        const tt = ticketTypes?.[0] || resolveTicketType(seat.categoryId, seat.category?.name)
        addSeatToSelection(seat, tt || undefined)
      }
    }
  }, [selectedSeats, cart.items, removeCartItem, categoryToTicketTypes, resolveTicketType, addSeatToSelection, showToast, isUnitSection, singleSeatCount, selectionCap])

  // Handle ticket type picker confirmation
  const handleTicketTypePickerConfirm = useCallback(() => {
    if (!ticketTypePicker) return
    const tt = ticketTypePicker.ticketTypes.find(t => t.id === ticketTypePicker.selectedTicketTypeId)
    addSeatToSelection(ticketTypePicker.seat, tt)
    setTicketTypePicker(null)
  }, [ticketTypePicker, addSeatToSelection])

  // Seat hover from unified map
  const handleSeatHover = useCallback((seat: ComputedSeatPosition | null, x: number, y: number) => {
    if (seat) {
      setTooltipSeat({
        seatId: seat.seatId,
        seatNumber: seat.seatNumber,
        seatIndex: seat.seatIndex,
        rowLabel: seat.rowLabel,
        rowNumber: seat.rowNumber,
        seatType: seat.seatType,
        status: seat.status,
        categoryId: seat.categoryId,
        category: seat.category,
        price: seat.price,
        x: seat.absX,
        y: seat.absY,
      })
      setTooltipSectionKey(seat.sectionKey)
      setTooltipPos({ x, y })
      // All-in price for the tooltip: the seat's own (a per-seat price carries
      // its own fees) before the ticket type's
      const seatAllIn = (seat as { allInPrice?: number | null }).allInPrice
      const tt = resolveTicketType(seat.categoryId, seat.category?.name)
      if (seatAllIn != null && seatAllIn > 0) {
        setTooltipAllInPrice(seatAllIn)
      } else if (tt && tt.allInPrice > 0) {
        setTooltipAllInPrice(tt.allInPrice)
      } else {
        setTooltipAllInPrice(null)
      }
    } else {
      setTooltipSeat(null)
      setTooltipAllInPrice(null)
    }
  }, [feeRatio, resolveTicketType])

  // Remove all seats from a section (for whole-table, variable, GA, BOOTH)
  const handleRemoveSection = useCallback((sectionKey: string) => {
    setSelectedSeats(prev => {
      const next = new Map(prev)
      for (const [id, s] of prev) {
        if (s.sectionKey === sectionKey) {
          next.delete(id)
          const cartItem = cart.items.find(i => i.seatId === id)
          if (cartItem) removeCartItem(cartItem.id)
        }
      }
      return next
    })
  }, [cart.items, removeCartItem])

  const handleRemoveSeat = useCallback((seatId: string) => {
    // Check if this seat belongs to a unit section — if so, remove all seats from that section
    const seatInfo = selectedSeats.get(seatId)
    if (seatInfo?.sectionKey && isUnitSection(seatInfo.sectionKey)) {
      handleRemoveSection(seatInfo.sectionKey)
      return
    }
    // Fallback: if sectionType is TABLE, remove all seats with same sectionKey or sectionName
    if (seatInfo?.sectionType === 'TABLE' && (seatInfo.sectionKey || seatInfo.sectionName)) {
      const matchKey = seatInfo.sectionKey || seatInfo.sectionName
      setSelectedSeats(prev => {
        const next = new Map(prev)
        for (const [id, s] of prev) {
          if ((s.sectionKey || s.sectionName) === matchKey) {
            next.delete(id)
            const cartItem = cart.items.find(i => i.seatId === id)
            if (cartItem) removeCartItem(cartItem.id)
          }
        }
        return next
      })
      return
    }
    setSelectedSeats(prev => {
      const next = new Map(prev)
      next.delete(seatId)
      return next
    })
    // Also remove from cart if already added
    const cartItem = cart.items.find(i => i.seatId === seatId)
    if (cartItem) removeCartItem(cartItem.id)
  }, [cart.items, removeCartItem, selectedSeats, isUnitSection, handleRemoveSection])

  // Section click handler for GA, whole-table, and booth sections
  const handleSectionClick = useCallback((sectionKey: string, mode: 'GA' | 'WHOLE_TABLE' | 'BOOTH') => {
    // If this section already has selected seats, deselect all of them (toggle off)
    const hasSelectedSeats = Array.from(selectedSeats.values()).some(s => s.sectionKey === sectionKey)
    if (hasSelectedSeats) {
      handleRemoveSection(sectionKey)
      return
    }

    const sd = sections.find(s => s.sectionKey === sectionKey)
    if (!sd) return
    if (sd.available <= 0) {
      showToast('This section is sold out')
      return
    }

    // Show confirmation picker for all unit types
    setUnitPickerSection({ sectionKey, mode })
    setUnitPickerQty(1)
    setUnitPickerTypeQty({})
  }, [sections, showToast, selectedSeats, handleRemoveSection])

  // Select all seats in a table/booth unit.
  // typeQuantities (GA multi-type): ordered per-type counts, e.g.
  // [{id: adultId, qty: 2}, {id: childId, qty: 1}] — each generated seat is
  // stamped with its own ticket type so the cart sync (grouped by
  // sectionKey:ticketTypeId) carries a mixed order through checkout.
  const handleSelectWholeUnit = useCallback(async (sectionKey: string, mode: 'GA' | 'WHOLE_TABLE' | 'BOOTH', chosenTicketTypeId?: string, typeQuantities?: Array<{ id: string; qty: number }>) => {
    try {
      setUnitPickerLoading(true)
      const sectionUrl = seasonMode
        ? `/api/seasons/${seasonMode.seasonId}/seat-map/section/${sectionKey}`
        : `/api/events/${event.id}/seat-map/section/${sectionKey}${previewQs ? `?${previewQs}` : ''}`
      const res = await fetch(sectionUrl)
      const data = await res.json()
      if (!data.success || !data.data) {
        showToast('Failed to load section details')
        return
      }
      const detail = data.data
      const freeSeats = detail.rows.flatMap((r: { seats: Array<{ status: string }> }) =>
        r.seats.filter((s: { status: string }) => s.status === 'FREE')
      )

      // GA sections may not have individual seat records — use capacity from overview
      const sectionOverviewData = sections.find(s => s.sectionKey === sectionKey)
      const isGASection = mode === 'GA' || sectionOverviewData?.sectionType === 'GA'

      if (freeSeats.length === 0 && !isGASection) {
        // Mark section as sold out so it immediately grays out on the map
        setSections(prev => prev.map(s =>
          s.sectionKey === sectionKey ? { ...s, available: 0 } : s
        ))
        showToast('No available seats in this section')
        return
      }

      // For GA with no seat records, check the overview capacity
      if (freeSeats.length === 0 && isGASection && (sectionOverviewData?.available || 0) <= 0) {
        showToast('This section is sold out')
        return
      }

      const sectionOverview = sections.find(s => s.sectionKey === sectionKey)
      const sectionName = sectionOverview?.name || sectionKey
      const sectionType = sectionOverview?.sectionType || mode

      // For whole-table booking mode, ALL seats in the table must be free.
      // If some seats are held/sold, the table is unavailable for whole-table booking.
      const bookingMode = sectionOverview?.tableConfig?.bookingMode || sectionOverview?.bookingMode
      if (bookingMode === 'whole-table' && sectionOverview?.capacity && freeSeats.length < sectionOverview.capacity) {
        setSections(prev => prev.map(s =>
          s.sectionKey === sectionKey ? { ...s, available: 0 } : s
        ))
        showToast(`${sectionName} is not available — some seats are already held`)
        return
      }

      // GA multi-type: total across all per-type quantities; single-type
      // paths keep using the plain quantity stepper value.
      const activeTypeQuantities = mode === 'GA'
        ? (typeQuantities || []).filter(t => t.qty > 0)
        : []
      const gaRequestedQty = activeTypeQuantities.length > 0
        ? activeTypeQuantities.reduce((s, t) => s + t.qty, 0)
        : unitPickerQty

      // For GA sections with no physical seats, generate virtual seat objects
      let seatsToAdd: typeof freeSeats
      if (isGASection && freeSeats.length === 0 && (sectionOverviewData?.available || 0) > 0) {
        const qty = Math.min(gaRequestedQty, sectionOverviewData?.available || 0)
        seatsToAdd = Array.from({ length: qty }, (_, i) => ({
          seatId: `ga-${sectionKey}-${Date.now()}-${i}`,
          seatNumber: `GA-${i + 1}`,
          seatIndex: i,
          rowLabel: 'GA',
          rowNumber: 0,
          seatType: 'STANDARD',
          status: 'FREE',
          categoryId: sectionOverviewData?.categoryId || null,
          category: sectionOverviewData?.category || null,
          price: null,
          holdSessionId: null,
        }))
      } else {
        seatsToAdd = freeSeats.slice(0, mode === 'GA' ? gaRequestedQty : freeSeats.length)
      }

      // Resolve ticket type price for this section via category mapping.
      // Ticket type price is the admin-configured price — use it instead of
      // EventSeatStatus.price which may be stale/incorrect. When the GA picker
      // offered a choice (category maps to multiple types), that choice wins.
      const categoryTypes = sectionOverview?.categoryId
        ? categoryToTicketTypes.get(sectionOverview.categoryId)
        : undefined
      const chosenTT = chosenTicketTypeId
        ? categoryTypes?.find(t => t.id === chosenTicketTypeId)
        : undefined
      const matchedTT = chosenTT || resolveTicketType(sectionOverview?.categoryId)

      // Per-seat type assignment for GA multi-type: walk the quantities in
      // order so seat i gets the type its position falls into.
      const typeForSeatIndex = (idx: number) => {
        if (activeTypeQuantities.length === 0) return matchedTT
        let cursor = 0
        for (const t of activeTypeQuantities) {
          cursor += t.qty
          if (idx < cursor) return categoryTypes?.find(ct => ct.id === t.id) || matchedTT
        }
        return matchedTT
      }

      // A per-table price is the WHOLE table: split it over the server's seat
      // count and order (seatCount / pricingIndex from the section endpoint),
      // never over the free seats alone or the overview capacity. GA sections
      // never divide, whatever their layout says.
      const tableIndex = isPerTableSection(sectionOverview) ? buildPerTableIndex(detail) : null
      if (tableIndex) perTableIndexRef.current.set(sectionKey, tableIndex)

      setSelectedSeats(prev => {
        const next = new Map(prev)
        for (const [seatIdx, seat] of seatsToAdd.entries()) {
          // GA multi-type: each seat may carry a different assigned type
          const seatTT = typeForSeatIndex(seatIdx)
          const ttPrice = seatTT?.price ?? 0
          const ttAllIn = seatTT?.allInPrice ?? 0
          // Baked seat price wins over the live ticket-type price — matches
          // the cart's charge order (EventSeatStatus first). GA multi-type
          // seats are virtual (no baked price), so the assigned type's
          // price applies. In SEASON mode the baked price is one game's, not
          // the pass's, so the season tier always wins (see addSeatToSelection).
          const rawPrice = seasonMode
            ? seasonPassSeatPrice(ttPrice)
            : activeTypeQuantities.length > 0 ? ttPrice : (seat.price ?? ttPrice)
          let basePrice: number
          if (tableIndex && rawPrice > 0 && tableIndex.count > 1) {
            const i = tableIndex.indexById.get(seat.seatId) ?? seatIdx
            basePrice = perTableShare(rawPrice, tableIndex.count, i)
          } else if (rawPrice > 0) {
            // Admin price is per-seat (default) — use it directly
            basePrice = rawPrice
          } else {
            basePrice = seat.price ?? 0
          }
          // The page's one all-in helper on the per-seat price (a table's
          // SHARE): the same source as the price key, Add to Cart and Best
          // Available. A season pass keeps its own type's all-in figure.
          let allIn: number
          if (seasonMode && ttAllIn > 0) {
            allIn = tableIndex && rawPrice > 0 && tableIndex.count > 1
              ? perTableShare(ttAllIn, tableIndex.count, tableIndex.indexById.get(seat.seatId) ?? seatIdx)
              : ttAllIn
          } else {
            allIn = allInFor(basePrice)
          }
          next.set(seat.seatId, {
            seatId: seat.seatId,
            seatNumber: seat.seatNumber || '',
            seatIndex: seat.seatIndex || 0,
            rowLabel: seat.rowLabel || (mode === 'GA' ? 'GA' : ''),
            rowNumber: seat.rowNumber || 0,
            seatType: seat.seatType || 'STANDARD',
            status: 'FREE',
            categoryId: seat.categoryId || null,
            category: seat.category || null,
            price: basePrice,
            x: 0,
            y: 0,
            sectionKey,
            sectionName,
            sectionType,
            // Carry the category-resolved ticket type so the cart labels
            // these seats correctly (omitting it made checkout fall back to
            // a guessed type). PRICES MUST BE THE PER-SEAT SHARE: these
            // fields feed the cart's `price` directly, and the raw ticket
            // type price is the WHOLE TABLE in per-table mode — carrying it
            // raw priced every seat at $650 ($3,900 for a 6-top at checkout).
            ticketTypeId: seatTT?.id,
            ticketTypeName: seatTT?.name,
            ticketTypePrice: basePrice,
            ticketTypeAllInPrice: allIn,
            allInPrice: allIn,
          })
        }
        return next
      })
      showToast(`Added ${seatsToAdd.length} ${mode === 'GA' ? 'ticket' : 'seat'}${seatsToAdd.length !== 1 ? 's' : ''}`)
    } catch {
      showToast('Failed to select seats')
    } finally {
      setUnitPickerLoading(false)
      setUnitPickerSection(null)
    }
  }, [event.id, sections, showToast, unitPickerQty, allInFor, resolveTicketType, categoryToTicketTypes, seasonMode])

  // GA quantity picker confirm — multi-type sections send per-type
  // quantities; single-type sections use the plain stepper value.
  const handleGAPickerConfirm = useCallback(() => {
    if (!unitPickerSection) return
    const typeQuantities = Object.entries(unitPickerTypeQty)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => ({ id, qty }))
    if (typeQuantities.length > 0) {
      handleSelectWholeUnit(unitPickerSection.sectionKey, 'GA', undefined, typeQuantities)
      return
    }
    handleSelectWholeUnit(unitPickerSection.sectionKey, 'GA', unitPickerSection.selectedTicketTypeId)
  }, [unitPickerSection, unitPickerTypeQty, handleSelectWholeUnit])

  // Confirm selection: batch-hold seats, then add to cart
  // Turn a Best Available result into a priced selection. Shared by the
  // panel's pick and the hold-time retry, which must price the replacement
  // seats exactly the way the original pick was priced.
  const buildBestAvailableSelection = useCallback(async (seats: BestAvailableSeat[]): Promise<Map<string, SelectedSeatInfo>> => {
    const newSelected = new Map<string, SelectedSeatInfo>()
    // Per-table sections split over the server's seat count and order, so a
    // best-available pick of two seats at a six-top prices them as the seats
    // they ARE at the table, not as seats 1 and 2 of the pick.
    const tableIndexes = new Map<string, PerTableIndex | null>()
    for (const seat of seats) {
      if (tableIndexes.has(seat.sectionKey)) continue
      if (!isPerTableSection(sections.find(s => s.sectionKey === seat.sectionKey))) continue
      tableIndexes.set(seat.sectionKey, await loadPerTableIndex(seat.sectionKey))
    }
    for (const seat of seats) {
      const matchedTT = resolveTicketType(seat.categoryId)
      const sectionOv = sections.find(s => s.sectionKey === seat.sectionKey)
      // Baked seat price first — the charge path prefers it over the live
      // ticket-type price (see addSeatToSelection).
      //
      // NOT IN SEASON MODE. Best Available is answered by the EVENT endpoint
      // (/api/events/[id]/seats/best-available resolves a season id to its
      // first event), so `seat.price` here is that one game's
      // EventSeatStatus.price. Trusting it is how a $200 season pass reached
      // the cart — and the checkout summary — priced at a single game's $11,
      // while the server charged the real $200.
      let basePrice = seasonMode
        ? seasonPassSeatPrice(matchedTT?.price)
        : (seat.price ?? matchedTT?.price ?? 0)
      if (isPerTableSection(sectionOv) && basePrice > 0) {
        const tableIndex = tableIndexes.get(seat.sectionKey)
        const count = tableIndex?.count ?? (sectionOv?.capacity || 1)
        if (count > 1) {
          const i = tableIndex?.indexById.get(seat.seatId) ?? 0
          basePrice = perTableShare(basePrice, count, i)
        }
      }
      // Same all-in source as the price key and Add to Cart, applied to the
      // per-seat share (see addSeatToSelection). A season pass carries no fees.
      const allIn = seasonMode ? basePrice : allInFor(basePrice)
      newSelected.set(seat.seatId, {
        seatId: seat.seatId,
        seatNumber: seat.seatNumber,
        seatIndex: seat.seatIndex,
        rowLabel: seat.rowLabel,
        rowNumber: seat.rowNumber,
        seatType: 'STANDARD',
        status: 'FREE',
        categoryId: seat.categoryId,
        category: categories.find(c => c.id === seat.categoryId) || null,
        price: basePrice,
        x: 0,
        y: 0,
        sectionKey: seat.sectionKey,
        sectionName: seat.sectionName,
        // Carry the category-resolved ticket type so the cart labels these
        // seats correctly (omitting it made checkout fall back to a guess).
        // Prices must be the PER-SEAT share (basePrice/allIn are divided
        // above for per-table sections) — the raw type price is the whole
        // table and feeds the cart's price directly.
        ticketTypeId: matchedTT?.id,
        ticketTypeName: matchedTT?.name,
        ticketTypePrice: basePrice,
        ticketTypeAllInPrice: allIn,
        allInPrice: allIn,
      })
    }
    return newSelected
  }, [categories, sections, allInFor, resolveTicketType, loadPerTableIndex, seasonMode])

  // A Best Available card quotes the pick exactly as it would be selected:
  // priced by buildBestAvailableSelection, totalled like the Add to Cart bar.
  const priceBestAvailableResult = useCallback(async (seats: BestAvailableSeat[]) => {
    const list = Array.from((await buildBestAvailableSelection(seats)).values())
    const perSeat = list.map(s => s.allInPrice)
    return {
      perSeat: perSeat.length > 0 && perSeat.every(p => p === perSeat[0]) ? perSeat[0] : null,
      total: allInTotalFor(list),
    }
  }, [buildBestAvailableSelection, allInTotalFor])

  const handleConfirmSelection = useCallback(async () => {
    if (selectedSeats.size === 0) return

    // A flex pass is only a pass once its games are chosen. The season page
    // collects them and this screen carries them; if they are somehow missing
    // (a hand-edited URL), stop here rather than build a cart line the server
    // will refuse after the card has been charged.
    if (seasonMode?.passType === 'flex' && !seasonMode.selectedEventIds?.length) {
      showToast('Please choose your games for this pass before selecting seats.')
      router.push(`/seasons/${event.slug}`)
      return
    }

    // Validate minOccupancy for variable-mode tables before proceeding
    const sectionSeatCounts = new Map<string, number>()
    for (const [, s] of selectedSeats) {
      if (s.sectionKey) {
        sectionSeatCounts.set(s.sectionKey, (sectionSeatCounts.get(s.sectionKey) || 0) + 1)
      }
    }
    for (const [sectionKey, count] of sectionSeatCounts) {
      const info = sectionBookingModes.get(sectionKey)
      if (info?.bookingMode === 'variable' && info.minOccupancy && count < info.minOccupancy) {
        const sectionName = sectionNameMap.get(sectionKey) || sectionKey
        showToast(`${sectionName} requires a minimum of ${info.minOccupancy} seats`)
        return
      }
    }

    setIsAdding(true)
    setError(null)
    // What this press holds, so a failure anywhere after the hold gives it back
    // at once instead of leaving it HELD in nobody's cart until the hold runs
    // out (lib/seat-map/seat-cart-attempt).
    let attempt: SeatCartAttempt | null = null
    let hadCartSeats = false
    const seatCartRequests = {
      addToCart: async (ticketTypeId: string, seatIds: string[]) => {
        const cartRes = await fetch('/api/cart/items', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            eventId: event.id,
            ticketTypeId,
            seatIds,
            sessionId,
          }),
        })
        const cartData = await cartRes.json()
        return { ok: !!cartData.success, error: cartData.error, cartId: cartData.data?.cartId ?? null }
      },
      removeFromCart: async (seatIds: string[]) => {
        await Promise.all(seatIds.map(seatId => fetch('/api/cart/items/remove-seat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eventId: event.id, seatId, sessionId }),
        })))
        markOwnHolds(seatIds, false)
      },
      releaseHolds: async (seatIds: string[]) => {
        await fetch(`/api/events/${event.id}/seats/release`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ seatIds, sessionId }),
        })
        markOwnHolds(seatIds, false)
      },
    }
    try {
      // The selection can change below: an untouched Best Available pick that
      // collides with another buyer is moved to the next best seats and held
      // again. Read seats from this map, not from state, so everything after
      // the hold sees the seats that were ACTUALLY held.
      let seatsMap: Map<string, SelectedSeatInfo> = selectedSeats

      // Determine which seats are NEW (not already held in cart) vs already held.
      // GA virtual seats never hold — they use capacity-based inventory.
      const existingCartSeatIds = new Set(
        cart.items.filter(i => i.seatId && i.eventId === event.id).map(i => i.seatId!)
      )
      hadCartSeats = existingCartSeatIds.size > 0
      const newIdsOf = (m: Map<string, SelectedSeatInfo>) =>
        Array.from(m.keys()).filter(id => !id.startsWith('ga-') && !existingCartSeatIds.has(id))
      let newSeatIds = newIdsOf(seatsMap)

      // Step 1: Create server-side holds for NEW real seats only
      let heldSeatIds: string[] = [...existingCartSeatIds] // Start with already-held seats
      let movedToNextBest = false
      if (newSeatIds.length > 0) {
        const holdUrl = seasonMode
          ? `/api/seasons/${seasonMode.seasonId}/seats/hold`
          : `/api/events/${event.id}/seats/hold`
        const additive = !seasonMode && existingCartSeatIds.size > 0
        const hold = async (seatIds: string[]): Promise<HoldOutcome> => {
          // While this is in flight, a poll showing these seats HELD is our
          // own hold landing, not another buyer's.
          for (const id of seatIds) holdingRef.current.add(id)
          try {
            const res = await fetch(holdUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ seatIds, sessionId, additive }),
            })
            const d = await res.json()
            if (d.success) markOwnHolds((d.data?.heldSeatIds as string[] | undefined) || seatIds, true)
            return { ok: !!d.success, code: d.code, error: d.error, failedSeatIds: d.failedSeatIds, data: d.data }
          } finally {
            for (const id of seatIds) holdingRef.current.delete(id)
          }
        }
        // Only an UNTOUCHED Best Available pick is moved on a collision. A
        // buyer who edited it, or picked seats by hand, chose those seats.
        const lastPick = lastBestAvailableRef.current
        const untouchedPick = !!lastPick && !seasonMode
          && seatsMap.size === lastPick.seatIds.size
          && Array.from(seatsMap.keys()).every(id => lastPick.seatIds.has(id))
        const result = await holdWithRetry(newSeatIds, {
          hold,
          research: untouchedPick
            ? async () => (await searchBestAvailable(event.id, lastPick!.search)).options[0]?.seats ?? null
            : null,
          reselect: async (seats) => {
            const next = await buildBestAvailableSelection(seats)
            seatsMap = next
            setSelectedSeats(next)
            lastBestAvailableRef.current = { search: lastPick!.search, seatIds: new Set(next.keys()) }
            return newIdsOf(next)
          },
        })
        newSeatIds = result.seatIds
        movedToNextBest = result.outcome.ok && result.collisions > 0
        const holdData = result.outcome
        const holdInfo = (holdData.data || {}) as { expiresAt?: string; heldSeatIds?: string[]; singleSeatWarning?: string }

        if (!holdData.ok) {
          // No-single-seat rule = "block": stop the buyer and explain the fix.
          if (holdData.code === 'SINGLE_SEAT_BLOCKED') {
            showToast(holdData.error || 'That selection would leave a single seat by itself. Please adjust your seats.')
            setIsAdding(false)
            return
          }
          if (result.collisions > 0) {
            // The pick AND every next-best alternative were taken. Clear the
            // pick so the Best Available panel comes back for a fresh search.
            setSelectedSeats(new Map())
            lastBestAvailableRef.current = null
            throw new Error('Those seats were taken while you were choosing, and so were the next best. Please search again.')
          }
          // Check for partial failure
          const failedIds = new Set<string>(holdData.failedSeatIds || [])
          if (failedIds.size > 0 && failedIds.size < newSeatIds.length) {
            // Partial failure - some seats taken
            const failedNames = Array.from(seatsMap.values())
              .filter(s => failedIds.has(s.seatId))
              .map(s => `Row ${s.rowLabel} Seat ${s.seatNumber}`)
              .join(', ')
            showToast(`Seats taken by another customer: ${failedNames}`)
            setSelectedSeats(prev => {
              const next = new Map(prev)
              for (const id of failedIds) next.delete(id)
              return next
            })
            setIsAdding(false)
            return
          }
          // Total failure
          throw new Error(holdData.error || 'These seats may have been taken by another customer. Please select different seats.')
        }

        // Held. Season passes add nothing to the server cart here (and hold
        // across the season's events), so there is nothing to give back.
        if (!seasonMode) {
          attempt = startSeatCartAttempt(
            (holdInfo.heldSeatIds || newSeatIds).filter(id => !existingCartSeatIds.has(id)),
            seatCartRequests
          )
        }

        if (movedToNextBest) {
          // The buyer is about to see different seats at checkout — say so.
          showToast('Your first choice was just taken. We moved you to the next best seats.')
        }

        // No-single-seat rule = "warn": allowed through, but flag it to the buyer.
        if (holdInfo.singleSeatWarning) {
          showToast(holdInfo.singleSeatWarning)
        }

        // Store hold expiration for checkout page timer
        // IMPORTANT: Never increase the timer - use the earlier of existing vs new expiry
        if (holdInfo.expiresAt) {
          const newExpiry = holdInfo.expiresAt
          const existingExpiry = localStorage.getItem(`seat-hold-expires-${event.id}`)
          if (existingExpiry && new Date(existingExpiry).getTime() < new Date(newExpiry).getTime()) {
            // Keep the existing earlier expiration — timer should never go up
          } else {
            localStorage.setItem(`seat-hold-expires-${event.id}`, newExpiry)
            setHoldExpiresAt(new Date(newExpiry))
          }
          localStorage.setItem(`seat-hold-session-${event.id}`, sessionId)
        }

        heldSeatIds = [...heldSeatIds, ...(holdInfo.heldSeatIds || newSeatIds)]
      }

      const seats = Array.from(seatsMap.values())
      const gaSeats = seats.filter(s => s.seatId.startsWith('ga-'))

      // Step 2: Add only NEW held seats to server cart (skip for season mode)
      const newHeldSeatIds = newSeatIds.filter(id => heldSeatIds.includes(id))

      if (newHeldSeatIds.length > 0 && !seasonMode) {
        // Group seats by their selected ticket type for proper cart items
        const seatsByTicketType = new Map<string, string[]>()
        for (const seatId of newHeldSeatIds) {
          const seatInfo = seatsMap.get(seatId)
          // No resolved type → send '' and let the server resolve from the
          // seat's category mapping. Guessing ticketTypes[0] here mislabeled
          // whole-table seats as the first type in the list ("1st Row
          // Admission" on $650 VIP tables).
          const ttId = seatInfo?.ticketTypeId || ''
          const existing = seatsByTicketType.get(ttId) || []
          existing.push(seatId)
          seatsByTicketType.set(ttId, existing)
        }

        if (!attempt) attempt = startSeatCartAttempt(newHeldSeatIds, seatCartRequests)
        for (const [ticketTypeId, seatIdsForType] of seatsByTicketType) {
          // Throws when the cart refuses the seats; the catch below gives
          // back everything this press held.
          const cartId = await attempt.add(ticketTypeId, seatIdsForType)

          // Store server cart ID
          if (cartId) {
            localStorage.setItem('seatCartId', cartId)
          }
        }
      }

      // Step 2b: Add GA virtual seats to server cart (no seat holds needed)
      if (gaSeats.length > 0 && !seasonMode) {
        // Group GA seats by section AND the ticket type stamped at selection
        // time — the GA picker may have chosen a non-default type for the
        // category, and re-resolving here would silently revert it.
        const gaSeatsBySection = new Map<string, typeof gaSeats>()
        for (const gs of gaSeats) {
          const key = `${gs.sectionKey}:${gs.ticketTypeId || ''}`
          const arr = gaSeatsBySection.get(key) || []
          arr.push(gs)
          gaSeatsBySection.set(key, arr)
        }
        for (const [, sectionGaSeats] of gaSeatsBySection) {
          const first = sectionGaSeats[0]
          const tt = (first.ticketTypeId && event.ticketTypes.find(t => t.id === first.ticketTypeId))
            || resolveTicketType(first.categoryId, first.category?.name)
            || event.ticketTypes[0]
          if (tt) {
            const cartRes = await fetch('/api/cart/items', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                eventId: event.id,
                ticketTypeId: tt.id,
                quantity: sectionGaSeats.length,
                sessionId,
              }),
            })
            const cartData = await cartRes.json().catch(() => ({}))
            // A refused standing-room add (sold out, zone full) used to be
            // ignored: the buyer was sent to checkout with tickets the server
            // never reserved, and found an empty or short cart there. Stop
            // here and say why; the catch below gives back the seats this
            // press held.
            if (!cartRes.ok || !cartData.success) {
              throw new Error(cartData.error || `Could not add ${first.sectionName || 'these tickets'} to your cart. Please try again.`)
            }
            // The quantity route answers the cart id at the top level.
            const gaCartId = cartData.cartId || cartData.data?.cartId
            if (gaCartId) {
              localStorage.setItem('seatCartId', gaCartId)
            }
          }
        }
      }

      // Step 3: Add only NEW seats to client-side cart
      const newSeats = seats.filter(s => !existingCartSeatIds.has(s.seatId))
      for (const seat of newSeats) {
        // Use the per-seat ticket type chosen by the picker, or fall back to category-based lookup
        let ticketType: typeof event.ticketTypes[0] | undefined
        if (seat.ticketTypeId) {
          ticketType = event.ticketTypes.find(t => t.id === seat.ticketTypeId)
        }
        if (!ticketType) {
          ticketType = resolveTicketType(seat.categoryId, seat.category?.name) || event.ticketTypes[0]
        }

        // `seat.price` is a per-GAME price in season mode and must never win
        // there — the tier price stamped at selection time is the pass price.
        const basePrice = seasonMode
          ? seasonPassSeatPrice(seat.ticketTypePrice ?? ticketType?.price)
          : (seat.ticketTypePrice ?? seat.price ?? ticketType?.price ?? 0)
        const allInPrice = seasonMode ? basePrice : (seat.ticketTypeAllInPrice ?? ticketType?.allInPrice ?? basePrice)
        addItem({
          eventId: seasonMode ? seasonMode.seasonId : event.id,
          eventTitle: seasonMode ? `${seasonMode.seasonName} - ${seat.ticketTypeName || ticketType?.name || 'Season Pass'}` : event.name,
          eventStart: Math.floor(new Date(event.startDate).getTime() / 1000),
          ticketTypeId: ticketType?.id || '',
          ticketTypeName: seat.ticketTypeName || ticketType?.name || 'Seat',
          quantity: 1,
          price: basePrice,
          allInPrice,
          seatId: seat.seatId,
          seatData: {
            sectionName: seat.sectionName || '',
            sectionKey: seat.sectionKey,
            sectionType: seat.sectionType,
            rowLabel: seat.rowLabel,
            seatNumber: seat.seatNumber,
            categoryName: seat.category?.name,
          },
          ...(seasonMode ? {
            seasonId: seasonMode.seasonId,
            seasonTicketTypeId: seasonMode.seasonTicketTypeId || ticketType?.id,
            seasonPassType: seasonMode.passType,
            seasonName: seasonMode.seasonName,
            // The games a flex buyer chose, validated by the season page
            // before this screen rendered. Sending nothing is what let a flex
            // pass be issued against the whole season, or against nothing.
            seasonSelectedEventIds: seasonMode.selectedEventIds ?? null,
          } : {}),
        }, { openDrawer: false })
      }
      // The browser's cart has the seats now: nothing after this gives them back.
      attempt = null

      // Smooth transition overlay before navigation
      setNavigating(true)
      await new Promise(r => setTimeout(r, 100))
      router.push('/checkout')
    } catch (err) {
      // Give back what this press held, now: otherwise the seats sit HELD in
      // nobody's cart until the hold runs out. A refused cart add once took
      // two VIP tables off sale for 17 minutes this way.
      if (attempt) {
        void attempt.undo()
        if (!hadCartSeats) {
          // Nothing of this event is held any more, so no countdown either.
          localStorage.removeItem(`seat-hold-expires-${event.id}`)
          setHoldExpiresAt(null)
        }
      }
      setError(err instanceof Error ? err.message : 'Failed to confirm selection')
      setNavigating(false)
    } finally {
      setIsAdding(false)
    }
  }, [selectedSeats, event, sessionId, addItem, router, resolveTicketType, showToast, cart.items, sectionBookingModes, sectionNameMap, seasonMode, buildBestAvailableSelection, markOwnHolds])

  // Extend hold expiration
  const handleManualExtend = useCallback(async () => {
    try {
      const res = await fetch(`/api/events/${event.id}/seats/extend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      })
      const data = await res.json()

      if (data.success && data.expiresAt) {
        const newExpiry = new Date(data.expiresAt)
        setHoldExpiresAt(newExpiry)
        localStorage.setItem(`seat-hold-expires-${event.id}`, data.expiresAt)
        showToast(`Hold extended! You have ${Math.ceil((newExpiry.getTime() - Date.now()) / 60000)} more minutes.`)
      } else {
        throw new Error(data.error || 'Failed to extend hold')
      }
    } catch (err) {
      showToast('Failed to extend hold. Please complete your selection.')
      console.error('Hold extension error:', err)
    }
  }, [event.id, sessionId, showToast])

  // Handle hold expiration
  const handleHoldExpired = useCallback(() => {
    setHoldExpiresAt(null)
    setSelectedSeats(new Map())
    showToast('Your seat hold has expired. Please select your seats again.')
  }, [showToast])

  // Cancel - no server release needed since seats are only locally selected
  const handleCancel = useCallback(() => {
    router.push('/')
  }, [router])

  // Best available handler — receives full seat data from the button (single API call)

  const handleBestAvailableFound = useCallback(async (seats: BestAvailableSeat[], search?: BestAvailableSearch) => {
    const newSelected = await buildBestAvailableSelection(seats)
    setSelectedSeats(newSelected)
    // Whether the pick is still untouched is decided at hold time, by
    // comparing the selection to this set — so hand edits need no bookkeeping.
    lastBestAvailableRef.current = search ? { search, seatIds: new Set(newSelected.keys()) } : null

    // Zoom to the section of the first best-available seat
    if (seats.length > 0 && seats[0].sectionKey) {
      mapRef.current?.zoomToSection(seats[0].sectionKey)
    }
  }, [buildBestAvailableSelection])

  const selectedSeatIds = useMemo(() => new Set(selectedSeats.keys()), [selectedSeats])
  // The Best Available finder searches SEATS — on charts with only GA
  // (capacity-based, seatless) sections it can only ever report "0 available"
  // while the section chips correctly show capacity. Hide it there.
  const focusedSectionName = focusedSectionKey ? sections.find(s => s.sectionKey === focusedSectionKey)?.name ?? null : null
  const hasSeatedInventory = useMemo(
    () => sections.some(s => s.sectionType !== 'GA' && s.sectionType !== 'STAGE' && (s.available ?? 0) > 0),
    [sections]
  )
  // Categories offered in the Best Available dropdown: drop those carried
  // ONLY by GA sections — the finder searches seats, GA is capacity-based,
  // so those categories can never return anything but "0 available".
  // Categories used on no section at all (e.g. per-row categories) are kept.
  const findableCategories = useMemo(() => categories.filter(c => {
    const carrying = sections.filter(s => s.categoryId === c.id)
    return carrying.length === 0 || carrying.some(s => s.sectionType !== 'GA')
  }), [categories, sections])
  // Derive which sections (booths/tables) have all their seats selected
  const selectedSectionKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const seat of selectedSeats.values()) {
      if (seat.sectionKey) keys.add(seat.sectionKey)
    }
    return keys
  }, [selectedSeats])
  const selectedSeatsList = useMemo(() => Array.from(selectedSeats.values()), [selectedSeats])
  const totalPrice = selectedSeatsList.reduce((sum, s) => sum + (s.price || 0), 0)

  /** The all-in total for the WHOLE selection (see allInTotalFor). */
  const totalAllInPrice = useMemo(() => allInTotalFor(selectedSeatsList), [selectedSeatsList, allInTotalFor])

  // Is the map away from its overview (zoomed in or panned)? Reported by the
  // map itself, which knows the real fitted frame — estimating it here from
  // the designer's whole canvas read a fitted chart as "zoomed" (GCA).
  const [isZoomedIn, setIsZoomedIn] = useState(false)
  // A collapsed Best Available sheet starts closed again after seats are picked
  useEffect(() => { if (selectedSeats.size > 0) setBestSheetOpen(false) }, [selectedSeats.size])

  // Minimap navigation - route through the map ref to update its internal transform
  const handleMinimapNavigate = useCallback((x: number, y: number) => {
    mapRef.current?.navigateTo(x, y)
  }, [])

  // Loading state — show skeleton with venue section placeholders
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-surface flex flex-col" style={{ height: '100dvh' }}>
        {/* Header with event info */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-surface-border">
          <button onClick={() => router.back()} className="p-1.5 rounded-full hover:bg-surface-secondary text-text-secondary">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-text-primary truncate">{event.name}</h1>
            <div className="flex items-center gap-2 text-xs text-text-secondary">
              <Calendar className="w-3 h-3 flex-shrink-0" />
              <span>{formattedDate} at {formattedTime}</span>
              {formattedDoorTime && <span className="ml-1 text-text-tertiary">| Doors {formattedDoorTime}</span>}
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col lg:flex-row min-h-0">
          {/* Map skeleton with section placeholders */}
          <div className="h-[70dvh] lg:h-auto lg:flex-1 flex items-center justify-center bg-surface-secondary/20">
            <div className="w-[80%] max-w-xl aspect-[4/3] relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-6 bg-surface-secondary rounded-b-lg animate-pulse" />
              <div className="absolute top-12 left-[10%] w-[25%] h-[35%] bg-surface-secondary/50 rounded-lg animate-pulse" />
              <div className="absolute top-12 left-[38%] w-[24%] h-[35%] bg-surface-secondary/50 rounded-lg animate-pulse" style={{ animationDelay: '150ms' }} />
              <div className="absolute top-12 right-[10%] w-[25%] h-[35%] bg-surface-secondary/50 rounded-lg animate-pulse" style={{ animationDelay: '300ms' }} />
              <div className="absolute bottom-[15%] left-[5%] w-[30%] h-[30%] bg-surface-secondary/30 rounded-lg animate-pulse" style={{ animationDelay: '100ms' }} />
              <div className="absolute bottom-[15%] right-[5%] w-[30%] h-[30%] bg-surface-secondary/30 rounded-lg animate-pulse" style={{ animationDelay: '250ms' }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-10 h-10 border-2 border-[rgb(var(--color-primary))] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-text-secondary text-sm">Loading seat map...</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar skeleton (desktop) */}
          <div className="hidden lg:flex flex-col w-[380px] border-l border-surface-border p-4 gap-4">
            <div className="space-y-3">
              <div className="h-4 w-24 bg-surface-secondary rounded animate-pulse" />
              {[0, 1, 2].map(i => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-surface-secondary animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
                  <div className="h-3 w-20 bg-surface-secondary rounded animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
                  <div className="ml-auto h-3 w-12 bg-surface-secondary rounded animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state: no layout after loading finished. The render below
  // dereferences layout (focalPoint, dimensions) unguarded, so continuing
  // with null was a guaranteed client-side crash — a black screen with
  // "Application error" — whenever the overview API returned nothing
  // (archived event, chart detached, transient failure).
  if (!layout) {
    return (
      <div className="fixed inset-0 z-50 bg-surface flex flex-col items-center justify-center gap-4 px-6 text-center" style={{ height: '100dvh' }}>
        <h1 className="text-lg font-semibold text-text-primary">Seat map unavailable</h1>
        <p className="text-sm text-text-secondary max-w-md">
          We couldn&apos;t load the seat map for this event right now. Please refresh the page, or come
          back in a moment — if this keeps happening, tickets may not be on sale yet.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[rgb(var(--color-primary))] text-text-inverse rounded-lg text-sm font-semibold"
          >
            Refresh
          </button>
          <button
            onClick={() => router.back()}
            className="px-4 py-2 border border-surface-border text-text-primary rounded-lg text-sm font-semibold"
          >
            Back to event
          </button>
        </div>
      </div>
    )
  }

  // Clean transition: render ONLY the overlay when navigating to checkout
  // This prevents the sidebar/map from flashing during the page transition
  if (navigating) {
    return (
      <div className="fixed inset-0 z-50 bg-surface flex items-center justify-center" style={{ height: '100dvh' }}>
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[rgb(var(--color-primary))] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary text-sm">Preparing checkout...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-surface flex flex-col" style={{ height: '100dvh' }}>
      {/* Hide parent layout chrome while seat map is full-screen */}
      <style>{`nav.sticky, footer, [data-cart-drawer] { display: none !important; }`}</style>

      {/* Error banner */}
      {error && (
        <div className="bg-error/10 border-b border-error/20 px-4 py-2 text-center">
          <p className="text-error text-sm">{error}</p>
        </div>
      )}

      {/* Flex pass: the games already chosen, so the buyer can see what this
          seat is being bought for — and change it without losing their place. */}
      {seasonMode?.passType === 'flex' && !!seasonMode.selectedEventIds?.length && (
        <div className="bg-surface-secondary border-b border-surface-border px-4 py-2.5 flex items-center gap-3">
          <p className="text-xs text-text-secondary flex-1 min-w-0">
            <span className="font-semibold text-text-primary">
              {seasonMode.selectedEventIds.length} game{seasonMode.selectedEventIds.length !== 1 ? 's' : ''} chosen
            </span>
            {!!seasonMode.selectedEventNames?.length && (
              <span className="hidden sm:inline">: {seasonMode.selectedEventNames.join(', ')}</span>
            )}
          </p>
          <button
            onClick={() => router.push(`/seasons/${event.slug}`)}
            className="flex-shrink-0 min-h-[44px] px-3 text-xs font-semibold text-[rgb(var(--color-primary))] hover:underline"
          >
            Change games
          </button>
        </div>
      )}

      {/* Toast notification */}
      {toastMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] max-w-md">
          <div className="bg-surface-tertiary text-text-primary rounded-lg px-4 py-2.5 text-sm shadow-lg flex items-center gap-2 border border-surface-border">
            <span>{toastMessage}</span>
            <button onClick={() => setToastMessage(null)} className="p-0.5 hover:text-text-tertiary">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Mobile header: the desktop sidebar (back link, event name, date) is
          hidden below lg, which left phones with no way back and no idea
          which event they were choosing seats for. One line, in the layout
          flow, so it never covers the map. */}
      <div className="lg:hidden flex items-center gap-2 pl-1 pr-3 border-b border-surface-border bg-surface flex-shrink-0">
        <button
          onClick={handleCancel}
          aria-label="Back to events"
          className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0 flex items-baseline gap-2">
          <h1 className="text-sm font-semibold text-text-primary truncate">{event.name}</h1>
          <span className="flex-shrink-0 text-xs text-text-secondary whitespace-nowrap">{formattedDate}</span>
        </div>
      </div>

      {/* Main layout: map + panel */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Map area — constrained height on mobile, flexible on desktop */}
        <div className={`${isFullscreenMap ? 'fixed inset-0 z-50 bg-surface' : 'flex-1'} flex min-w-0 min-h-0`}>
          {/* Map area */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0 relative">
            {/* Loading fallback when layout hasn't loaded yet */}
            {!layout && loading && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-10 h-10 border-2 border-[rgb(var(--color-primary))] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-text-secondary text-sm">Loading seat map...</p>
                </div>
              </div>
            )}
            {/* Error/empty fallback when layout failed to load */}
            {!layout && !loading && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center max-w-sm px-6">
                  <p className="text-text-secondary mb-4 text-sm">
                    {error || 'Seat map is not available for this event.'}
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button onClick={loadOverview} className="px-4 py-2 bg-[rgb(var(--color-primary))] text-text-inverse rounded-lg text-sm font-medium">
                      Try Again
                    </button>
                    <button onClick={() => router.push('/')} className="px-4 py-2 border border-surface-border text-text-secondary rounded-lg text-sm">
                      Back to Events
                    </button>
                  </div>
                </div>
              </div>
            )}
            {layout && (
              <>
                {/* Mobile level selector (horizontal strip at top) */}
                {floors.length > 1 && (
                  <div className="lg:hidden flex gap-1 px-3 py-2 bg-surface border-b border-surface-border overflow-x-auto">
                    <button
                      onClick={() => setActiveFloor(null)}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        activeFloor === null
                          ? 'bg-[rgb(var(--color-primary))] text-text-inverse'
                          : 'text-text-secondary bg-surface-secondary'
                      }`}
                    >
                      All
                    </button>
                    {floors.map(f => (
                      <button
                        key={f.number}
                        onClick={() => setActiveFloor(f.number)}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                          activeFloor === f.number
                            ? 'bg-[rgb(var(--color-primary))] text-text-inverse'
                            : 'text-text-secondary bg-surface-secondary'
                        }`}
                      >
                        {f.name}
                      </button>
                    ))}
                  </div>
                )}

                {/* Fullscreen header bar (desktop only) */}
                {isFullscreenMap && (
                  <div className="hidden lg:flex absolute top-0 left-0 right-0 z-30 items-center justify-between px-4 py-3 bg-surface/95 backdrop-blur-sm border-b border-surface-border">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setIsFullscreenMap(false)}
                        className="p-2 hover:bg-surface-secondary rounded-lg text-text-secondary transition-colors"
                        aria-label="Exit fullscreen"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <span className="text-sm font-semibold text-text-primary">{event.name}</span>
                    </div>
                    <div className="text-xs text-text-tertiary">
                      {selectedSeats.size > 0 ? `${selectedSeats.size} seat${selectedSeats.size > 1 ? 's' : ''} selected` : 'Press ESC to exit'}
                    </div>
                  </div>
                )}

                {/* Mobile price key: a compact strip of category dots (the
                    desktop sidebar's Pricing list is hidden below lg). In the
                    layout flow, so the map fits below it, not under it. */}
                {categoryLegend.length > 0 && (
                  <div className="lg:hidden flex-shrink-0 flex items-center gap-3 px-3 py-1.5 bg-surface border-b border-surface-border overflow-x-auto whitespace-nowrap" aria-label="Price categories">
                    {categoryLegend.map(c => (
                      <span key={c.id} className="flex items-center gap-1.5 text-[11px] text-text-secondary">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                        <span className="font-medium text-text-primary">{c.name}</span>
                        <span className="text-text-tertiary">{formatPrice(c.price)}{c.perTable ? ' per table' : ''}</span>
                      </span>
                    ))}
                  </div>
                )}

                {/* The map and what floats over it, measured as one box */}
                <div ref={mapContainerRef} className="flex-1 flex flex-col min-w-0 min-h-0 relative">
                {/* Mobile: Overview button overlaid on map (only when zoomed in) */}
                {/* The map hides a focused section's own label, so the name
                    rides beside the button: a phone zoomed onto one table
                    otherwise showed seats 1-8 and no hint which table. */}
                {isZoomedIn && (
                  <div className="lg:hidden absolute top-2 left-3 right-3 z-20 flex items-center gap-2 pointer-events-none">
                    <button
                      onClick={() => mapRef.current?.zoomToOverview()}
                      className="pointer-events-auto flex-shrink-0 flex items-center gap-1.5 px-3 py-2 min-h-[44px] bg-surface/90 backdrop-blur-sm border border-surface-border rounded-lg text-sm font-medium text-text-secondary shadow-sm"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Overview
                    </button>
                    {focusedSectionName && (
                      <span className="min-w-0 truncate px-3 py-1.5 bg-surface/90 backdrop-blur-sm border border-surface-border rounded-lg text-sm font-semibold text-text-primary shadow-sm">
                        {focusedSectionName}
                      </span>
                    )}
                  </div>
                )}

                <UnifiedSeatMap
                  vizPreview={vizPreview}
                  highlightCategoryId={highlightCategoryId}
                  ref={mapRef}
                  eventId={event.id}
                  // In season mode `event.id` is the SEASON id, so the map's
                  // own seat fetch must go to the season endpoint — the one
                  // that reads BOTH pricing bindings. Left on /api/events it
                  // fell into that route's season branch, which knows only
                  // `seatCategoryId` and prices a season configured on the
                  // Seat Pricing Map from an arbitrary tier.
                  sectionFetchUrlPrefix={seasonMode ? '/api/seasons' : undefined}
                  layout={layout}
                  sections={sections}
                  categories={categories}
                  selectedSeatIds={selectedSeatIds}
                  selectedSectionKeys={selectedSectionKeys}
                  onSeatClick={handleSeatClick}
                  onSeatHover={handleSeatHover}
                  onSectionFocus={(key) => { setFocusedSectionKey(key); setShowViewImage(false) }}
                  onTransformChange={mapTransformStore.set}
                  onZoomedChange={setIsZoomedIn}
                  onFloorSwitch={setActiveFloor}
                  onSectionClick={handleSectionClick}
                  venueShape={venueShape}
                  stage={chartStage}
                  decorativeShapes={decorativeShapes}
                  layoutLabels={layoutLabels}
                  layoutImages={layoutImages}
                  backgroundImageUrl={backgroundImageUrl}
                  showRemainingQuantity={event.showSoldCount !== false}
                  activeFloor={floors.length > 1 ? activeFloor : null}
                  floors={floors.length > 1 ? floors : undefined}
                  sessionId={sessionId}
                  onError={showToast}
                  // The measured phone panel only. The old "80 when seats are
                  // selected" fallback also applied on desktop, where there is
                  // no bottom bar, and nudged the map up on every first pick.
                  bottomOffset={mobileBottomH}
                  highlightedSeatIds={previewSeatIds}
                  isFullscreen={isFullscreenMap}
                  onFullscreenToggle={setIsFullscreenMap}
                  bundledSeatData={seatBundle ?? bundledSeatDataRef.current}
                  seatBundlePending={seatBundlePending}
                />

                {/* Minimap (visible when zoomed in) */}
                <LiveSeatMapMinimap
                  layout={layout}
                  sections={sections}
                  categories={categories}
                  focusedSectionKey={focusedSectionKey}
                  venueShape={venueShape}
                  transformStore={mapTransformStore}
                  containerWidth={mapContainerSize.width}
                  containerHeight={mapContainerSize.height}
                  onNavigate={handleMinimapNavigate}
                  visible={isZoomedIn}
                />
                </div>
              </>
            )}

            {/* GA Quantity Picker - positioned over the map */}
            {unitPickerSection?.mode === 'GA' && (() => {
              const sd = sections.find(s => s.sectionKey === unitPickerSection.sectionKey)
              if (!sd) return null
              // A table booked whole does not use up the order's ticket slots.
              const remainingSlots = Math.max(0, 10 - singleSeatCount)
              const maxQty = Math.min(sd.available, remainingSlots)
              // Ticket type choice: a category can map to multiple types
              // (Adult/Child GA) — offer the same picker seats get.
              const gaTypes = (sd.categoryId ? categoryToTicketTypes.get(sd.categoryId) : undefined)
                ?.filter(t => !(t as { isAddon?: boolean }).isAddon) || []
              const selectedGAType = gaTypes.find(t => t.id === unitPickerSection.selectedTicketTypeId)
                || gaTypes[0]
                || resolveTicketType(sd.categoryId)
              const ticketDescription = selectedGAType?.description
              return (
                <div className="absolute inset-0 z-50 flex items-center justify-center p-4">
                  <div className="absolute inset-0 bg-black/40" onClick={() => setUnitPickerSection(null)} />
                  <div className="relative bg-surface rounded-2xl shadow-xl max-w-sm w-full p-6">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-text-primary">{sd.name}</h3>
                      {sd.accessible && (
                        <span className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs font-medium">
                          ADA
                        </span>
                      )}
                    </div>
                    {/* One type: its description up top. Several: each row carries its own. */}
                    {gaTypes.length <= 1 && ticketDescription && (
                      <div className="mb-3"><RichDescription html={ticketDescription} /></div>
                    )}
                    {gaTypes.length > 1 ? (() => {
                      // Per-type quantity steppers — one confirm adds a mixed
                      // order (e.g. 2 Adult + 1 Child)
                      const totalPicked = gaTypes.reduce((s, tt) => s + (unitPickerTypeQty[tt.id] || 0), 0)
                      const atCap = totalPicked >= maxQty
                      return (
                        <div className="space-y-2 mb-3">
                          <p className="text-xs font-medium text-text-tertiary uppercase tracking-wide">Tickets</p>
                          {gaTypes.map(tt => {
                            const qty = unitPickerTypeQty[tt.id] || 0
                            return (
                              <div
                                key={tt.id}
                                className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                                  qty > 0
                                    ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5'
                                    : 'border-surface-border'
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-text-primary truncate">{tt.name}</p>
                                  <p className="text-xs text-text-tertiary">${tt.allInPrice.toFixed(2)} each</p>
                                  {tt.description && (
                                    <RichDescription html={tt.description} className="text-xs text-text-tertiary mt-1" />
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => setUnitPickerTypeQty(prev => ({ ...prev, [tt.id]: Math.max(0, (prev[tt.id] || 0) - 1) }))}
                                    disabled={qty <= 0}
                                    className="w-11 h-11 flex items-center justify-center rounded-full border border-surface-border text-text-primary disabled:opacity-30 text-lg font-medium"
                                  >
                                    -
                                  </button>
                                  <span className="text-base font-bold text-text-primary w-7 text-center">{qty}</span>
                                  <button
                                    onClick={() => setUnitPickerTypeQty(prev => ({ ...prev, [tt.id]: (prev[tt.id] || 0) + 1 }))}
                                    disabled={atCap}
                                    className="w-11 h-11 flex items-center justify-center rounded-full border border-surface-border text-text-primary disabled:opacity-30 text-lg font-medium"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            )
                          })}
                          <p className="text-xs text-text-tertiary">All fees included.</p>
                          {event.showSoldCount !== false && (
                            <p className="text-xs text-text-tertiary">{sd.available} available</p>
                          )}
                          <div className="flex gap-3 pt-3">
                            <button
                              onClick={() => setUnitPickerSection(null)}
                              className="flex-1 min-h-[44px] py-2.5 border border-surface-border text-text-secondary rounded-xl text-sm"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleGAPickerConfirm}
                              disabled={unitPickerLoading || totalPicked === 0}
                              className="flex-1 min-h-[44px] py-2.5 bg-[rgb(var(--color-primary))] text-text-inverse rounded-xl font-semibold text-sm disabled:opacity-50"
                            >
                              {unitPickerLoading ? 'Adding...' : `Add ${totalPicked} Ticket${totalPicked !== 1 ? 's' : ''}`}
                            </button>
                          </div>
                        </div>
                      )
                    })() : (
                      <>
                        {sd.minPrice != null && (
                          <p className="text-sm font-medium text-text-primary mb-2">
                            ${(selectedGAType?.allInPrice ?? sd.allInMinPrice ?? sd.minPrice).toFixed(2)} per ticket
                            <span className="text-xs text-text-tertiary font-normal ml-1">all fees included</span>
                          </p>
                        )}
                        {event.showSoldCount !== false && (
                          <p className="text-xs text-text-tertiary mb-3">{sd.available} available</p>
                        )}

                        <div className="flex items-center justify-center gap-4 mb-6">
                          <button
                            onClick={() => setUnitPickerQty(q => Math.max(1, q - 1))}
                            disabled={unitPickerQty <= 1}
                            className="w-11 h-11 flex items-center justify-center rounded-full border border-surface-border text-text-primary disabled:opacity-30 text-lg font-medium"
                          >
                            -
                          </button>
                          <span className="text-2xl font-bold text-text-primary w-12 text-center">{unitPickerQty}</span>
                          <button
                            onClick={() => setUnitPickerQty(q => Math.min(maxQty, q + 1))}
                            disabled={unitPickerQty >= maxQty}
                            className="w-11 h-11 flex items-center justify-center rounded-full border border-surface-border text-text-primary disabled:opacity-30 text-lg font-medium"
                          >
                            +
                          </button>
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={() => setUnitPickerSection(null)}
                            className="flex-1 min-h-[44px] py-2.5 border border-surface-border text-text-secondary rounded-xl text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleGAPickerConfirm}
                            disabled={unitPickerLoading}
                            className="flex-1 min-h-[44px] py-2.5 bg-[rgb(var(--color-primary))] text-text-inverse rounded-xl font-semibold text-sm disabled:opacity-50"
                          >
                            {unitPickerLoading ? 'Adding...' : `Add ${unitPickerQty} Ticket${unitPickerQty !== 1 ? 's' : ''}`}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )
            })()}

            {/* Ticket Type Picker (shown when a category has multiple types like Adult/Child/Senior) */}
            {ticketTypePicker && (() => {
              const { seat, ticketTypes: pickerTypes, selectedTicketTypeId } = ticketTypePicker
              const sectionName = sectionNameMap.get(seat.sectionKey) || seat.sectionName || ''
              return (
                <div className="absolute inset-0 z-50 flex items-center justify-center p-4">
                  <div className="absolute inset-0 bg-black/40" onClick={() => setTicketTypePicker(null)} />
                  <div className="relative bg-surface rounded-2xl shadow-xl max-w-sm w-full p-6">
                    <div className="mb-1">
                      <h3 className="text-lg font-semibold text-text-primary">{sectionName}</h3>
                      <p className="text-sm text-text-secondary">
                        Row {seat.rowLabel}, Seat {seat.seatNumber}
                      </p>
                    </div>

                    <div className="border-t border-surface-border my-3" />

                    <p className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-2">Select ticket type</p>
                    <div className="space-y-2 mb-5">
                      {pickerTypes.map(tt => {
                        const isSelected = tt.id === selectedTicketTypeId
                        return (
                          <label
                            key={tt.id}
                            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                              isSelected
                                ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/5'
                                : 'border-surface-border hover:border-text-tertiary'
                            }`}
                          >
                            <input
                              type="radio"
                              name="ticket-type-picker"
                              checked={isSelected}
                              onChange={() =>
                                setTicketTypePicker(prev => prev ? { ...prev, selectedTicketTypeId: tt.id } : null)
                              }
                              className="sr-only"
                            />
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected ? 'border-[rgb(var(--color-primary))]' : 'border-text-tertiary'
                            }`}>
                              {isSelected && (
                                <div className="w-2 h-2 rounded-full bg-[rgb(var(--color-primary))]" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="block text-sm font-medium text-text-primary">{tt.name}</span>
                              {tt.description && (
                                <RichDescription html={tt.description} className="text-xs text-text-tertiary" />
                              )}
                            </div>
                            <span className="text-sm font-semibold text-text-primary">${tt.allInPrice.toFixed(2)}</span>
                          </label>
                        )
                      })}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => setTicketTypePicker(null)}
                        className="flex-1 min-h-[44px] py-2.5 border border-surface-border text-text-secondary rounded-xl text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleTicketTypePickerConfirm}
                        className="flex-1 min-h-[44px] py-2.5 bg-[rgb(var(--color-primary))] text-text-inverse rounded-xl font-semibold text-sm"
                      >
                        Select Seat
                      </button>
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* Table / Booth Confirmation Picker */}
            {unitPickerSection && (unitPickerSection.mode === 'WHOLE_TABLE' || unitPickerSection.mode === 'BOOTH') && (() => {
              const sd = sections.find(s => s.sectionKey === unitPickerSection.sectionKey)
              if (!sd) return null
              const matchedTT = resolveTicketType(sd.categoryId)
              const ticketDescription = matchedTT?.description
              const seatCount = sd.capacity || sd.available
              return (
                <div className="absolute inset-0 z-50 flex items-center justify-center p-4">
                  <div className="absolute inset-0 bg-black/40" onClick={() => setUnitPickerSection(null)} />
                  <div className="relative bg-surface rounded-2xl shadow-xl max-w-sm w-full p-6">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-text-primary">{sd.name}</h3>
                      {sd.accessible && (
                        <span className="px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs font-medium">
                          ADA
                        </span>
                      )}
                    </div>
                    {sd.viewImageUrl && (
                      <img
                        src={sd.viewImageUrl}
                        alt={`View from ${sd.name}`}
                        className="w-full h-36 object-cover rounded-xl mb-3"
                      />
                    )}
                    {ticketDescription && (
                      <div className="mb-3"><RichDescription html={ticketDescription} /></div>
                    )}
                    {sd.minPrice != null && (() => {
                      const isWholeTable = sd.tableConfig?.bookingMode === 'whole-table'
                      return isWholeTable ? (
                        <>
                          <p className="text-sm font-medium text-text-primary mb-1">
                            ${(sd.allInMinPrice ?? sd.minPrice).toFixed(2)} per table
                            <span className="text-xs text-text-tertiary font-normal ml-1">all fees included</span>
                          </p>
                          {seatCount > 0 && (
                            <p className="text-xs text-text-tertiary mb-6">
                              {seatCount} seat{seatCount !== 1 ? 's' : ''} included
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-text-primary mb-2">
                            ${(sd.allInMinPrice ?? sd.minPrice).toFixed(2)} per seat
                            <span className="text-xs text-text-tertiary font-normal ml-1">all fees included</span>
                          </p>
                          {seatCount > 0 && event.showSoldCount !== false && (
                            <p className="text-xs text-text-tertiary mb-6">{seatCount} seat{seatCount !== 1 ? 's' : ''}</p>
                          )}
                        </>
                      )
                    })()}
                    <div className="flex gap-3">
                      <button
                        onClick={() => setUnitPickerSection(null)}
                        className="flex-1 min-h-[44px] py-2.5 border border-surface-border text-text-secondary rounded-xl text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSelectWholeUnit(unitPickerSection.sectionKey, unitPickerSection.mode)}
                        disabled={unitPickerLoading}
                        className="flex-1 min-h-[44px] py-2.5 bg-[rgb(var(--color-primary))] text-text-inverse rounded-xl font-semibold text-sm disabled:opacity-50"
                      >
                        {unitPickerLoading ? 'Reserving...' : 'Reserve Table'}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })()}
          </div>
        </div>

        {/* Right: Sidebar (desktop only, hidden in fullscreen) */}
        {!isFullscreenMap && <div className="hidden lg:flex w-[360px] xl:w-[400px] flex-shrink-0 border-l border-surface-border flex-col bg-surface">
          {/* Back to Events */}
          <div className="px-5 py-3 border-b border-surface-border">
            <button
              onClick={handleCancel}
              className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Events
            </button>
          </div>

          {/* Event info */}
          <div className="px-5 py-3 border-b border-surface-border">
            <h2 className="text-base font-semibold text-text-primary leading-tight mb-1.5">{event.name}</h2>
            <div className="space-y-1 text-sm text-text-secondary">
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{formattedDate} at {formattedTime}</span>
              </div>
              {event.venue.name && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{event.venue.name}{event.venue.city ? `, ${event.venue.city}` : ''}</span>
                </div>
              )}
            </div>
          </div>

          {/* Pricing legend (C1) — section colors map to price tiers */}
          {categoryLegend.length > 0 && (
            <div className="px-5 py-3 border-b border-surface-border">
              <div className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-2">Pricing</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {/* Hovering a tier fades every other category's seats on the
                    map so buyers can see where a price lives (seats.io-style) */}
                {categoryLegend.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onMouseEnter={() => setHighlightCategoryId(c.id)}
                    onMouseLeave={() => setHighlightCategoryId(null)}
                    onFocus={() => setHighlightCategoryId(c.id)}
                    onBlur={() => setHighlightCategoryId(null)}
                    className="flex items-center gap-1.5 text-xs text-text-secondary cursor-default"
                  >
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                    <span className="font-medium text-text-primary">{c.name}</span>
                    <span className="text-text-tertiary">{formatPrice(c.price)}{c.perTable ? ' per table' : ''}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Selected seats summary + view from section (scrollable) */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 py-3">
              <SeatSelectionSummary
                selectedSeats={selectedSeatsList}
                orderTotal={totalAllInPrice}
                soldOut={everythingSold}
                onRemoveSeat={handleRemoveSeat}
                onRemoveSection={handleRemoveSection}
                accessibleSections={new Set(sections.filter(s => s.accessible).map(s => s.name))}
                sectionBookingModes={sectionBookingModes}
                maxSeats={selectionCap}
              />
            </div>

            {/* View from Section — inside scrollable area so it's always visible */}
            {focusedSectionKey && (() => {
              const sec = sections.find(s => s.sectionKey === focusedSectionKey)
              return sec?.viewImageUrl ? (
                <div className="px-5 py-3 border-t border-surface-border">
                  <p className="text-xs font-medium text-text-tertiary uppercase tracking-wide mb-2">View from {sec.name}</p>
                  <button onClick={() => setShowViewImage(true)} className="w-full text-left">
                    <img
                      src={sec.viewImageUrl}
                      alt={`View from ${sec.name}`}
                      className="w-full h-40 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                    />
                  </button>
                </div>
              ) : null
            })()}
          </div>

          {/* Best Available panel — hide after seats are selected, show view-from-seat image instead */}
          {!event.hideBestAvailable && hasSeatedInventory && selectedSeats.size === 0 && (
            <div className="px-5 py-3 border-t border-surface-border">
              <BestAvailablePanel
                eventId={event.id}
                categories={findableCategories}
                sections={sections}
                onSeatsFound={handleBestAvailableFound}
                onSeatsPreview={(ids) => setPreviewSeatIds(ids ? new Set(ids) : undefined)}
                sectionKey={focusedSectionKey}
                focalPoint={layout.focalPoint || null}
                quantity={desiredQty}
                onQuantityChange={setDesiredQtyClamped}
                priceResult={priceBestAvailableResult}
              />
            </div>
          )}

          {/* View from seats handled by the section view panel above */}

          {/* Hold Timer */}
          {holdExpiresAt && (
            <div className="px-5 py-2 border-t border-surface-border">
              <HoldTimer
                expiresAt={holdExpiresAt}
                onExpired={handleHoldExpired}
                onManualExtend={handleManualExtend}
              />
            </div>
          )}

          {/* Sticky bottom actions */}
          <div className="px-5 py-4 border-t border-surface-border bg-surface space-y-2">
            {selectedSeats.size > 0 && (
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-text-secondary">{selectedSeats.size} seat{selectedSeats.size !== 1 ? 's' : ''} selected</span>
                <span className="font-semibold text-text-primary">${totalAllInPrice.toFixed(2)}</span>
              </div>
            )}
            <button
              onClick={handleConfirmSelection}
              disabled={selectedSeats.size === 0 || isAdding}
              className="w-full py-3 bg-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-40 disabled:cursor-not-allowed text-text-inverse rounded-xl font-semibold text-sm transition-colors"
            >
              {isAdding ? 'Adding to Cart...' : selectedSeats.size > 0 ? 'Add to Cart' : 'Select Seats to Continue'}
            </button>
            <button
              onClick={handleCancel}
              className="w-full py-2.5 border border-surface-border text-text-secondary hover:text-text-primary rounded-xl text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>}

        {/* Mobile: Best Available panel when no seats selected */}
        {/* On a short screen (landscape phone) the ~290px form left ~120px of
            map, so it collapses to one button that opens it as a sheet. The
            ref stays on the outer box: bottomOffset is always its real height. */}
        {selectedSeats.size === 0 && !event.hideBestAvailable && hasSeatedInventory && (
          <div ref={mobileBottomRef} className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-surface border-t border-surface-border px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
            {shortViewport && !bestSheetOpen ? (
              <button
                type="button"
                onClick={() => setBestSheetOpen(true)}
                className="w-full min-h-[44px] flex items-center justify-center gap-2 border border-surface-border rounded-xl text-sm font-semibold text-text-primary bg-surface hover:bg-surface-secondary transition-colors"
              >
                <Star className="w-4 h-4" />
                Find best seats
              </button>
            ) : (
              <div className={shortViewport ? 'relative max-h-[80dvh] overflow-y-auto' : undefined}>
                {shortViewport && (
                  <button
                    type="button"
                    onClick={() => setBestSheetOpen(false)}
                    aria-label="Close best available"
                    className="absolute top-0 right-0 z-10 w-11 h-11 flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <BestAvailablePanel
                  eventId={event.id}
                  categories={findableCategories}
                  sections={sections}
                  onSeatsFound={handleBestAvailableFound}
                  onSeatsPreview={(ids) => setPreviewSeatIds(ids ? new Set(ids) : undefined)}
                  sectionKey={focusedSectionKey}
                  focalPoint={layout.focalPoint || null}
                  quantity={desiredQty}
                  onQuantityChange={setDesiredQtyClamped}
                  priceResult={priceBestAvailableResult}
                />
              </div>
            )}
          </div>
        )}

        {/* Mobile: sold-out notice. The desktop sidebar says so, but phones
            hide the sidebar and drop the Best Available form when nothing is
            left, so the map alone (grey seats, a 9px "Sold Out" on one block)
            was all a buyer saw. */}
        {everythingSold && selectedSeats.size === 0 && (
          <div ref={mobileBottomRef} className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-surface border-t border-surface-border px-4 py-3 flex items-center gap-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary">This event is sold out</p>
              <p className="text-xs text-text-secondary">Every seat has been sold.</p>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-shrink-0 min-h-[44px] px-4 border border-surface-border rounded-xl text-sm font-medium text-text-primary bg-surface hover:bg-surface-secondary transition-colors"
            >
              Browse events
            </button>
          </div>
        )}

        {/* Mobile: Fixed bottom CTA — only appears when seats are selected.
            The chevron opens a bottom sheet with the full per-seat summary
            (the same panel desktop shows) so mobile buyers can review and
            remove individual seats — previously they only ever saw a total. */}
        {selectedSeats.size > 0 && (
          <div ref={mobileBottomRef} className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-surface border-t border-surface-border" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
            {mobileSheetOpen && (
              <div className="max-h-[45vh] overflow-y-auto border-b border-surface-border px-4 py-3">
                <SeatSelectionSummary
                  selectedSeats={selectedSeatsList}
                  orderTotal={totalAllInPrice}
                  soldOut={everythingSold}
                  onRemoveSeat={handleRemoveSeat}
                  onRemoveSection={handleRemoveSection}
                  accessibleSections={new Set(sections.filter(s => s.accessible).map(s => s.name))}
                  sectionBookingModes={sectionBookingModes}
                  maxSeats={selectionCap}
                />
              </div>
            )}
            <div className="flex items-center gap-2 px-4 py-3">
              <button
                onClick={() => setMobileSheetOpen(o => !o)}
                aria-expanded={mobileSheetOpen}
                aria-label={mobileSheetOpen ? 'Hide selected seats' : 'Show selected seats'}
                className="flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl border border-surface-border text-text-primary"
              >
                <svg className={`w-5 h-5 transition-transform ${mobileSheetOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
              <button
                onClick={handleConfirmSelection}
                disabled={isAdding}
                className="flex-1 min-h-[44px] py-3 bg-[rgb(var(--color-primary))] text-text-inverse disabled:opacity-40 rounded-xl font-semibold text-sm transition-colors truncate"
              >
                {isAdding ? 'Adding...' : `Add ${selectedSeats.size} Seat${selectedSeats.size !== 1 ? 's' : ''} to Cart - ${formatPrice(totalAllInPrice)}`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View from seat enlargement modal */}
      {showViewImage && focusedSectionKey && (() => {
        const sec = sections.find(s => s.sectionKey === focusedSectionKey)
        if (!sec?.viewImageUrl) return null
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
            onClick={() => setShowViewImage(false)}
          >
            <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
              <img
                src={sec.viewImageUrl}
                alt={`View from ${sec.name}`}
                className="w-full rounded-xl"
              />
              <p className="text-white text-center mt-3 text-sm font-medium">
                View from {sec.name}
              </p>
              <button
                onClick={() => setShowViewImage(false)}
                className="absolute -top-3 -right-3 p-2 bg-surface rounded-full shadow-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )
      })()}

      {/* Tooltip */}
      <SeatTooltip
        seat={tooltipSeat}
        sectionName={tooltipSectionKey ? (sections.find(s => s.sectionKey === tooltipSectionKey)?.name || undefined) : undefined}
        sectionDescription={tooltipSectionKey ? ((layout.sections?.find((s: any) => s.key === tooltipSectionKey) as any)?.description || null) : null}
        sectionViewImageUrl={tooltipSectionKey ? (sections.find(s => s.sectionKey === tooltipSectionKey)?.viewImageUrl || null) : null}
        x={tooltipPos.x}
        y={tooltipPos.y}
        allInPrice={tooltipAllInPrice}
      />


    </div>
  )
}
